import * as i0 from '@angular/core';
import { Injectable, Component, Input, EventEmitter, Output, NgModule } from '@angular/core';
import { Subject, debounceTime } from 'rxjs';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { css, keyframes } from '@emotion/css';
import * as i1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import axios from 'axios';

class ScreenProvider {
    currentScreen = 'warning';
    screenChangeSource = new Subject();
    screenChangeObservable = this.screenChangeSource.asObservable();
    screenTransition(newScreen) {
        this.currentScreen = newScreen;
        this.screenChangeSource.next();
    }
    getCurrentScreen() {
        return this.currentScreen;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ScreenProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ScreenProvider, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ScreenProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const image = ({ width, height, margin }) => css `
  display: flex;
  width: ${width}px;
  height: ${height}px;
  margin: ${margin}px;
  border-radius: 5px;
`;
const imageContainer = css `
  display: flex;
  align-items: center;
  justify-content: center;
`;
const checkbox = css `
  display: flex;
  width: 17px;
  height: 15px;
  margin-left: 50px;
  margin-top: 50px;
`;
const svg$3 = ({ width, height, margin }) => css `
  display: flex;
  width: ${width}px;
  height: ${height}px;
  margin: ${margin};
`;
const pText = ({ display, fontWeight, fontSize, margin, textAlign, }) => css `
  display: ${display};
  font-weight: ${fontWeight};
  font-size: ${fontSize}px;
  margin: ${margin};
  text-align: ${textAlign};
`;
const input$3 = ({ display, width, height, margin }) => css `
  display: ${display};
  width: ${width}px;
  height: ${height}px;
  margin: ${margin};
`;
const buttonContainer$1 = css `
  display: flex;
  justify-content: center;
  margin-top: 50px;
  margin-bottom: 15px;
`;

const globalStyle = css `
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200;400;600&display=swap');
  font-family: 'Manrope', sans-serif;
`;
const simpleText = ({ fontSize, fontWeight, margin, color }) => css `
  font-size: ${fontSize}px;
  font-weight: ${fontWeight};
  margin: ${margin};
  color: ${color};

  @media screen and (max-width: 640px) {
    margin-left: '0px';
  }
`;
const scrollableContainer = css `
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 5px;
  }

  ::-webkit-scrollbar-thumb {
    background: #474747;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #d1d1d1;
  }
`;
const container$1 = ({ width }) => css `
  position: relative;
  width: ${width}px;
  height: auto;
  padding-top: 24px;

  @media screen and (max-width: 640px) {
    width: 100%;
  }
`;

const en_Us = {
    screens: {
        warning: {
            dataBelongsToYou: 'Your data belongs to you',
            uses: 'uses',
            connectProviders: 'to connect to Ag Data Providers',
            leafDisclamer: "Leaf doesn't distribute any data outside of",
            notShowAgain: 'Do not show this message again',
            continue: 'Continue',
        },
        fileUpload: {
            dragAndDrop: 'Browse or drag & drop your files here',
            zipOnly: 'zip files only',
            recentFiles: 'Recent files',
            last: 'Last',
            days: 'days',
            connectionError: 'Error in the widget connection. Please, check your apiKey and leafUserId',
            filesDisplay: 'Your uploaded files will be displayed here',
            filesAlreadyUploaded: 'One or many of your files were already Uploaded, please change the file(s) name(s)',
        },
        errorMessage: {
            notZip: 'is not supported. Only zip files are allowed.',
            generic: 'Sorry we were not able to process your request.',
        },
        uploadCard: {
            status: {
                processed: 'Processed',
                received: 'Received',
                failed: 'Failed',
            },
            uploading: 'Uploading...',
        },
        fileUploadOverlay: {
            dropFiles: 'Drop your files here to upload',
        },
        providerList: {
            selectIntegration: 'Select your integration',
            search: 'Search integrations',
            available: 'AVAILABLE',
            incomplete: 'INCOMPLETE',
            connected: 'CONNECTED',
            unauthenticated: 'AUTHENTICATION REQUIRED',
        },
        provider: {
            authenticateWith: 'Authenticate with',
            afterSelect: 'After you select to continue, you will be taken to',
            redirect: 'You will be directed back to',
            allowAccess: 'Allow access to your organization and done!',
            continue: 'Continue',
        },
        load: {
            authenticatingWith: 'Authenticating with',
        },
        organization: {
            authentication: 'Authentication',
            permissions: 'Permissions',
            organizations: 'Organizations',
            almostDone: 'Almost done',
            pleaseAllow: 'Please allow the access to the organizations',
            allow: 'Allow organizations',
            help: 'I need help',
            done: 'Done',
        },
        organizationCard: {
            allowed: 'Allowed',
            notAllowed: 'Not allowed',
        },
        done: {
            allDone: 'All done!',
            created: 'connection created successfully',
            add: 'Add other provider',
            done: 'Done',
        },
        authentication: {
            wantsToAccessYour: 'wants to access your',
            account: 'account.',
            continue: 'Continue',
        },
    },
};

const es_ES = {
    screens: {
        warning: {
            dataBelongsToYou: "Tus datos te pertenecen",
            uses: "utiliza",
            connectProviders: "para conectarse a proveedores de datos agrícolas",
            leafDisclamer: "Leaf no distribuye ningún dato fuera de",
            notShowAgain: "No mostrar este mensaje de nuevo",
            continue: "Continuar"
        },
        fileUpload: {
            dragAndDrop: "Explore o arrastre y suelte sus archivos aquí",
            zipOnly: "solo archivos zip",
            recentFiles: "Archivos recientes",
            last: "Últimos",
            days: "días",
            connectionError: "Error en la conexión del widget. Por favor, verifique su apiKey y leafUserId",
            filesDisplay: "Sus archivos cargados se mostrarán aquí",
            filesAlreadyUploaded: "Uno o varios de sus archivos ya se cargaron, cambie los nombres de los archivos"
        },
        errorMessage: {
            notZip: "no es apoyado. Solo se permiten archivos zip.",
            generic: "Lo sentimos, no pudimos procesar su solicitud."
        },
        uploadCard: {
            status: {
                processed: "Procesada",
                received: "Recibido",
                failed: "Fallido"
            },
            uploading: "Cargando..."
        },
        fileUploadOverlay: {
            dropFiles: "Suelte sus archivos aquí para cargar"
        },
        providerList: {
            selectIntegration: "Seleccione su integración",
            search: "Buscar integraciones",
            available: "DISPONIBLE",
            incomplete: "INCOMPLETO",
            connected: "CONECTADO",
            unauthenticated: "AUTENTICACION REQUERIDA"
        },
        provider: {
            authenticateWith: "Autenticación con",
            afterSelect: "Después de seleccionar Continuar, usted será redirigido al",
            redirect: "Usted será redirigido a",
            allowAccess: "Permita el acceso a su organización y ¡listo!",
            continue: "Continuar"
        },
        load: {
            authenticatingWith: "Autenticación con"
        },
        organization: {
            authentication: "Autenticación",
            permissions: "Permisos",
            organizations: "Organizaciones",
            almostDone: "Casi listo",
            pleaseAllow: "Conceda acceso a las organizaciones",
            allow: "Permitir a las organizaciones",
            help: "Necesito ayuda",
            done: "Concluir"
        },
        organizationCard: {
            allowed: "Permitido",
            notAllowed: "No permitido"
        },
        done: {
            allDone: "¡Listo!",
            created: "Conexión a creada con êxito",
            add: "Agregar otro proveedor",
            done: "Concluir"
        }
    }
};

const pt_BR = {
    screens: {
        warning: {
            dataBelongsToYou: 'Seus dados pertencem a você',
            uses: 'utiliza a',
            connectProviders: 'para conectar em provedores de dados',
            leafDisclamer: 'A Leaf não distribui nenhum dado fora da',
            notShowAgain: 'Não mostrar essa mensagem novamente',
            continue: 'Continuar',
        },
        fileUpload: {
            dragAndDrop: 'Navegue ou arraste e solte seus arquivos aqui',
            zipOnly: 'apenas arquivos zip',
            recentFiles: 'Arquivos recentes',
            last: 'Últimos',
            days: 'dias',
            connectionError: 'Erro na conexão do widget. Por favor, verifique sua apiKey e leafUserId',
            filesDisplay: 'Seus arquivos enviados serão exibidos aqui',
            filesAlreadyUploaded: 'Um ou mais de seus arquivos já foram carregados, altere o(s) nome(s) do(s) arquivo(s)',
        },
        errorMessage: {
            notZip: 'não é suportado. Somente arquivos zip são permitidos.',
            generic: 'Desculpe, não foi possível processar sua solicitação.',
        },
        uploadCard: {
            status: {
                processed: 'Processado',
                received: 'Recebido',
                failed: 'Falhou',
            },
            uploading: 'Enviando...',
        },
        fileUploadOverlay: {
            dropFiles: 'Solte seus arquivos aqui para fazer o upload',
        },
        providerList: {
            selectIntegration: 'Selecione sua integração',
            search: 'Buscar Integrações',
            available: 'DISPONÍVEL',
            incomplete: 'INCOMPLETO',
            connected: 'CONECTADO',
            unauthenticated: 'AUTENTICAÇÃO REQUERIDA',
        },
        provider: {
            authenticateWith: 'Autentique-se com',
            afterSelect: 'Depois de selecionar Continuar, você será redirecionado para',
            redirect: 'Você será redirecionado de volta para',
            allowAccess: 'Permita o acesso à sua organização e pronto!',
            continue: 'Continuar',
        },
        load: {
            authenticatingWith: 'Autenticando com',
        },
        organization: {
            authentication: 'Autenticação',
            permissions: 'Permissões',
            organizations: 'Organizações',
            almostDone: 'Quase pronto',
            pleaseAllow: 'Por favor conceda acesso às organizações',
            allow: 'Permitir Organizações',
            help: 'Preciso de ajuda',
            done: 'Concluir',
        },
        organizationCard: {
            allowed: 'Permitido',
            notAllowed: 'Não permitido',
        },
        done: {
            allDone: 'Tudo pronto!',
            created: 'conexão criada com sucesso',
            add: 'Adicionar outro provedor',
            done: 'Concluir',
        },
    },
};

const fr_FR = {
    screens: {
        warning: {
            dataBelongsToYou: 'Vos données vous appartiennent',
            uses: 'utilisations',
            connectProviders: 'pour se connecter aux fournisseurs de données Ag',
            leafDisclamer: "Leaf ne distribue aucune donnée à l'extérieur",
            notShowAgain: 'Ne pas afficher ce message à nouveau',
            continue: 'Continuer',
        },
        fileUpload: {
            dragAndDrop: 'Parcourir ou faire glisser et déposer vos fichiers ici',
            zipOnly: 'fichiers zip uniquement',
            recentFiles: 'Fichiers récents',
            last: 'Dernier',
            days: 'jours',
            connectionError: 'Erreur dans la connexion du widget. Veuillez vérifier votre apiKey et leafUserId',
            filesDisplay: 'Vos fichiers téléchargés seront affichés ici',
            filesAlreadyUploaded: 'Un ou plusieurs de vos fichiers ont déjà été téléchargés, veuillez changer le(s) nom(s) du/des fichier(s)',
        },
        errorMessage: {
            notZip: "n'est pas pris en charge. Seuls les fichiers zip sont autorisés.",
            generic: "Désolé, nous n'avons pas pu traiter votre demande.",
        },
        uploadCard: {
            status: {
                processed: 'Traité',
                received: 'Reçu',
                failed: 'Échoué',
            },
            uploading: 'Téléchargement en cours...',
        },
        fileUploadOverlay: {
            dropFiles: 'Déposez vos fichiers ici pour les télécharger',
        },
        providerList: {
            selectIntegration: 'Sélectionnez votre intégration',
            search: 'Rechercher des intégrations',
            available: 'DISPONIBLE',
            incomplete: 'INCOMPLET',
            connected: 'CONNECTÉ',
            unauthenticated: 'AUTHENTIFICATION REQUISE',
        },
        provider: {
            authenticateWith: "S'authentifier avec",
            afterSelect: 'Après avoir sélectionné continuer, vous serez redirigé vers',
            redirect: 'Vous serez redirigé vers',
            allowAccess: "Autoriser l'accès à votre organisation et terminé !",
            continue: 'Continuer',
        },
        load: {
            authenticatingWith: 'Authentification avec',
        },
        organization: {
            authentication: 'Authentification',
            permissions: 'Autorisations',
            organizations: 'Organisations',
            almostDone: 'Presque terminé',
            pleaseAllow: "Veuillez autoriser l'accès aux organisations",
            allow: 'Autoriser les organisations',
            help: "J'ai besoin d'aide",
            done: 'Terminé',
        },
        organizationCard: {
            allowed: 'Autorisé',
            notAllowed: 'Non autorisé',
        },
        done: {
            allDone: 'Tout est terminé !',
            created: 'Connexion créée avec succès',
            add: 'Ajouter un autre fournisseur',
            done: 'Terminé',
        },
        authentication: {
            wantsToAccessYour: 'souhaite accéder à votre',
            account: 'compte.',
            continue: 'Continuer',
        },
    },
};

class I18n {
    translate;
    constructor(translate) {
        this.translate = translate;
    }
    init(componentTranslation, lang) {
        const availableLangs = ['en_US', 'es_ES', 'pt_BR', 'fr_FR'];
        const fallbackLang = 'en_US';
        const browserLang = this.translate.getBrowserCultureLang()?.replace('-', '_') ?? fallbackLang;
        this.translate.setDefaultLang(fallbackLang);
        const selectedLang = availableLangs.includes(lang) ? lang : undefined;
        const navigatorLang = availableLangs.includes(browserLang)
            ? browserLang
            : fallbackLang;
        this.changeLanguage(selectedLang ?? navigatorLang);
        componentTranslation.setTranslation('en_US', en_Us);
        componentTranslation.setTranslation('es_ES', es_ES);
        componentTranslation.setTranslation('pt_BR', pt_BR);
        componentTranslation.setTranslation('fr_FR', fr_FR);
    }
    changeLanguage(lang) {
        this.translate.use(lang);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: I18n, deps: [{ token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: I18n, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: I18n, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.TranslateService }]; } });

const button = ({ padding, backgroundColor, color, fontSize, iconUrl, height }) => css `
  ${iconUrl && `
      display: flex;
      align-items: center;
      gap: 10px;
  `};
  padding: ${padding};
  width: fit-content;
  background-color: ${backgroundColor};
  border: 2px solid #0f876d;
  color: ${color};
  border-radius: 20px;
  height: ${height}px;
  font-weight: bold;
  font-size: ${fontSize}px;
  cursor: pointer;
`;
const iconStyle = css `
  height: 1em;
`;

class CustomButton {
    text;
    buttonStyle;
    globalStyle = globalStyle;
    button = button;
    iconStyle = iconStyle;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: CustomButton, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: CustomButton, selector: "custom-button", inputs: { text: "text", buttonStyle: "buttonStyle" }, ngImport: i0, template: "<button data-testid=\"custom-button\" [ngClass]=\"[globalStyle, button(buttonStyle)]\">\n  {{ text }}\n  <img *ngIf=\"buttonStyle.iconUrl\" [ngClass]=\"iconStyle\" [src]=\"buttonStyle.iconUrl\" alt=\"button-icon\">\n</button>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: CustomButton, decorators: [{
            type: Component,
            args: [{ selector: 'custom-button', template: "<button data-testid=\"custom-button\" [ngClass]=\"[globalStyle, button(buttonStyle)]\">\n  {{ text }}\n  <img *ngIf=\"buttonStyle.iconUrl\" [ngClass]=\"iconStyle\" [src]=\"buttonStyle.iconUrl\" alt=\"button-icon\">\n</button>\n" }]
        }], propDecorators: { text: [{
                type: Input
            }], buttonStyle: [{
                type: Input
            }] } });

class LeafLogo {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafLogo, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: LeafLogo, selector: "leaf-logo", ngImport: i0, template: "<svg viewBox=\"0 0 500 500\" xmlns=\"http://www.w3.org/2000/svg\">\n  <g id=\"g8\" transform=\"matrix(4.806167, 0, 0, -4.806167, -24.030832, 524.030884)\">\n    <g id=\"g10\" transform=\"scale(0.1)\">\n      <path d=\"m 116.852,623.223 v 392.547 c 0,4.26 3.453,7.71 7.714,7.71 h 278.825 c 4.261,0 7.714,3.45 7.714,7.72 v 51.41 c 0,4.27 -3.453,7.72 -7.714,7.72 H 57.7148 C 53.4531,1090.33 50,1086.88 50,1082.61 V 623.223 c 0,-4.266 3.4531,-7.715 7.7148,-7.715 h 51.4222 c 4.261,0 7.715,3.449 7.715,7.715\" style=\"fill:#183f34;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path12\"/>\n      <path d=\"M 957.988,63.1719 906.563,114.59 c -1.446,1.449 -3.411,2.262 -5.454,2.262 H 603.691 v 0.207 h -33.906 c -235.676,0.14 -429.847,181.097 -451.019,411.375 -0.368,3.992 -3.668,7.062 -7.672,7.062 H 59.5039 c -4.4922,0 -8.0742,-3.828 -7.707,-8.301 C 72.8711,271.449 279.789,67.5508 536.84,51.25 V 50 h 415.695 c 6.867,0 10.313,8.3086 5.453,13.1719\" style=\"fill:#2d876a;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path14\"/>\n      <path d=\"m 1023.27,570.445 v -0.156 l 0.21,-36.055 -1.15,-419.785 v 2.403 L 1089.18,50 h 1.15 v 553.488 h -1.25 c -17.18,271.36 -243.4,486.842 -519.017,486.842 h -71.235 c -4.258,0 -7.715,-3.45 -7.715,-7.72 v -51.41 c 0,-4.27 3.457,-7.72 7.715,-7.72 h 71.235 c 249.839,0 453.107,-203.218 453.207,-453.035\" style=\"fill:#2d876a;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path16\"/>\n      <path d=\"m 722.645,409.969 h -140.77 c -3.895,0.136 -7.797,0.207 -11.73,0.207 -0.004,0 -0.012,0 -0.016,0 h -0.399 c -88.093,0.168 -159.757,71.984 -159.757,160.09 v 152.379 c 0,4.257 3.449,7.714 7.707,7.714 h 152.383 c 88.046,0 159.863,-71.621 160.093,-159.48 l -0.004,-0.816 c 0,-3.864 0.067,-7.79 0.207,-11.68 v -54.805 l -86.531,86.531 c -1.445,1.446 -3.406,2.262 -5.453,2.262 h -72.73 c -6.872,0 -10.313,-8.312 -5.454,-13.168 L 727.543,411.77 c -1.332,-1.11 -3.027,-1.801 -4.898,-1.801 z m 299.925,-198.594 -78.8,78.793 v 272.023 l -0.043,1.762 c -0.11,2.027 -0.164,4.082 -0.164,6.11 v 0.816 c -0.325,205.613 -167.879,372.887 -373.5,372.887 v -66.848 c 168.82,0 306.382,-137.34 306.652,-305.93 v -0.925 c 0,-2.887 0.066,-5.813 0.203,-8.708 V 357.02 l -79.707,79.707 v 122.261 l -0.023,1.25 c -0.122,3.27 -0.184,6.575 -0.184,9.739 l 0.004,0.902 C 796.684,695.676 694.879,797.211 570.063,797.211 H 350.828 c -4.258,0 -7.715,-3.453 -7.715,-7.715 v -219.23 c 0,-124.891 101.606,-226.7 226.692,-226.95 l 0.465,0.008 c 3.187,0 6.476,-0.062 9.773,-0.187 l 209.453,-0.02 c 1.867,0 3.551,0.692 4.883,1.789 l 81.457,-81.496 h -0.07 l 66.855,-66.855 h 0.035 l 66.554,-66.586 13.17,-13.117 0.2,91.289 c 0,2.058 -0.01,3.234 -0.01,3.234\" style=\"fill:#183f34;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path18\"/>\n      <path d=\"m 760.004,261.148 c -1.445,1.45 -3.406,2.262 -5.453,2.262 H 578.922 c -2.887,0.133 -5.797,0.207 -8.656,0.207 l -0.711,-0.008 c -168.809,0.27 -306.149,137.84 -306.149,306.657 v 139.246 c 0,4.254 -3.449,7.707 -7.707,7.707 h -51.426 c -4.257,0 -7.714,-3.453 -7.714,-7.707 V 570.266 c 0,-205.625 167.281,-373.172 372.89,-373.504 l 0.817,0.004 c 2.004,0 4.047,-0.055 6.078,-0.161 l 229.613,-0.031 c 6.875,0 10.316,8.313 5.453,13.168 l -51.406,51.406\" style=\"fill:#183f34;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path20\"/>\n      <path d=\"M 570.063,876.918 H 271.121 c -4.258,0 -7.715,-3.453 -7.715,-7.715 v -64.254 c 0,-4.261 -3.449,-7.715 -7.707,-7.715 h -51.426 c -4.257,0 -7.714,3.454 -7.714,7.715 v 131.106 c 0,4.261 3.457,7.711 7.714,7.711 h 365.79 v -66.848\" style=\"fill:#2d876a;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path22\"/>\n    </g>\n  </g>\n</svg>" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafLogo, decorators: [{
            type: Component,
            args: [{ selector: 'leaf-logo', template: "<svg viewBox=\"0 0 500 500\" xmlns=\"http://www.w3.org/2000/svg\">\n  <g id=\"g8\" transform=\"matrix(4.806167, 0, 0, -4.806167, -24.030832, 524.030884)\">\n    <g id=\"g10\" transform=\"scale(0.1)\">\n      <path d=\"m 116.852,623.223 v 392.547 c 0,4.26 3.453,7.71 7.714,7.71 h 278.825 c 4.261,0 7.714,3.45 7.714,7.72 v 51.41 c 0,4.27 -3.453,7.72 -7.714,7.72 H 57.7148 C 53.4531,1090.33 50,1086.88 50,1082.61 V 623.223 c 0,-4.266 3.4531,-7.715 7.7148,-7.715 h 51.4222 c 4.261,0 7.715,3.449 7.715,7.715\" style=\"fill:#183f34;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path12\"/>\n      <path d=\"M 957.988,63.1719 906.563,114.59 c -1.446,1.449 -3.411,2.262 -5.454,2.262 H 603.691 v 0.207 h -33.906 c -235.676,0.14 -429.847,181.097 -451.019,411.375 -0.368,3.992 -3.668,7.062 -7.672,7.062 H 59.5039 c -4.4922,0 -8.0742,-3.828 -7.707,-8.301 C 72.8711,271.449 279.789,67.5508 536.84,51.25 V 50 h 415.695 c 6.867,0 10.313,8.3086 5.453,13.1719\" style=\"fill:#2d876a;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path14\"/>\n      <path d=\"m 1023.27,570.445 v -0.156 l 0.21,-36.055 -1.15,-419.785 v 2.403 L 1089.18,50 h 1.15 v 553.488 h -1.25 c -17.18,271.36 -243.4,486.842 -519.017,486.842 h -71.235 c -4.258,0 -7.715,-3.45 -7.715,-7.72 v -51.41 c 0,-4.27 3.457,-7.72 7.715,-7.72 h 71.235 c 249.839,0 453.107,-203.218 453.207,-453.035\" style=\"fill:#2d876a;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path16\"/>\n      <path d=\"m 722.645,409.969 h -140.77 c -3.895,0.136 -7.797,0.207 -11.73,0.207 -0.004,0 -0.012,0 -0.016,0 h -0.399 c -88.093,0.168 -159.757,71.984 -159.757,160.09 v 152.379 c 0,4.257 3.449,7.714 7.707,7.714 h 152.383 c 88.046,0 159.863,-71.621 160.093,-159.48 l -0.004,-0.816 c 0,-3.864 0.067,-7.79 0.207,-11.68 v -54.805 l -86.531,86.531 c -1.445,1.446 -3.406,2.262 -5.453,2.262 h -72.73 c -6.872,0 -10.313,-8.312 -5.454,-13.168 L 727.543,411.77 c -1.332,-1.11 -3.027,-1.801 -4.898,-1.801 z m 299.925,-198.594 -78.8,78.793 v 272.023 l -0.043,1.762 c -0.11,2.027 -0.164,4.082 -0.164,6.11 v 0.816 c -0.325,205.613 -167.879,372.887 -373.5,372.887 v -66.848 c 168.82,0 306.382,-137.34 306.652,-305.93 v -0.925 c 0,-2.887 0.066,-5.813 0.203,-8.708 V 357.02 l -79.707,79.707 v 122.261 l -0.023,1.25 c -0.122,3.27 -0.184,6.575 -0.184,9.739 l 0.004,0.902 C 796.684,695.676 694.879,797.211 570.063,797.211 H 350.828 c -4.258,0 -7.715,-3.453 -7.715,-7.715 v -219.23 c 0,-124.891 101.606,-226.7 226.692,-226.95 l 0.465,0.008 c 3.187,0 6.476,-0.062 9.773,-0.187 l 209.453,-0.02 c 1.867,0 3.551,0.692 4.883,1.789 l 81.457,-81.496 h -0.07 l 66.855,-66.855 h 0.035 l 66.554,-66.586 13.17,-13.117 0.2,91.289 c 0,2.058 -0.01,3.234 -0.01,3.234\" style=\"fill:#183f34;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path18\"/>\n      <path d=\"m 760.004,261.148 c -1.445,1.45 -3.406,2.262 -5.453,2.262 H 578.922 c -2.887,0.133 -5.797,0.207 -8.656,0.207 l -0.711,-0.008 c -168.809,0.27 -306.149,137.84 -306.149,306.657 v 139.246 c 0,4.254 -3.449,7.707 -7.707,7.707 h -51.426 c -4.257,0 -7.714,-3.453 -7.714,-7.707 V 570.266 c 0,-205.625 167.281,-373.172 372.89,-373.504 l 0.817,0.004 c 2.004,0 4.047,-0.055 6.078,-0.161 l 229.613,-0.031 c 6.875,0 10.316,8.313 5.453,13.168 l -51.406,51.406\" style=\"fill:#183f34;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path20\"/>\n      <path d=\"M 570.063,876.918 H 271.121 c -4.258,0 -7.715,-3.453 -7.715,-7.715 v -64.254 c 0,-4.261 -3.449,-7.715 -7.707,-7.715 h -51.426 c -4.257,0 -7.714,3.454 -7.714,7.715 v 131.106 c 0,4.261 3.457,7.711 7.714,7.711 h 365.79 v -66.848\" style=\"fill:#2d876a;fill-opacity:1;fill-rule:nonzero;stroke:none\" id=\"path22\"/>\n    </g>\n  </g>\n</svg>" }]
        }] });

class ArrowRightArrowLeftSolid {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ArrowRightArrowLeftSolid, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: ArrowRightArrowLeftSolid, selector: "arrow-right-arrow-left-solid", ngImport: i0, template: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 448 512\"><!--! Font Awesome Pro 6.2.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d=\"M438.6 150.6c12.5-12.5 12.5-32.8 0-45.3l-96-96c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.7 96 32 96C14.3 96 0 110.3 0 128s14.3 32 32 32l306.7 0-41.4 41.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l96-96zm-333.3 352c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 416 416 416c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0 41.4-41.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-96 96c-12.5 12.5-12.5 32.8 0 45.3l96 96z\"/></svg>" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ArrowRightArrowLeftSolid, decorators: [{
            type: Component,
            args: [{ selector: 'arrow-right-arrow-left-solid', template: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 448 512\"><!--! Font Awesome Pro 6.2.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d=\"M438.6 150.6c12.5-12.5 12.5-32.8 0-45.3l-96-96c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.7 96 32 96C14.3 96 0 110.3 0 128s14.3 32 32 32l306.7 0-41.4 41.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l96-96zm-333.3 352c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 416 416 416c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0 41.4-41.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-96 96c-12.5 12.5-12.5 32.8 0 45.3l96 96z\"/></svg>" }]
        }] });

class EyeSlashSolid {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: EyeSlashSolid, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: EyeSlashSolid, selector: "eye-slash-solid", ngImport: i0, template: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 640 512\"><!--! Font Awesome Pro 6.2.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d=\"M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c5.2-11.8 8-24.8 8-38.5c0-53-43-96-96-96c-2.8 0-5.6 .1-8.4 .4c5.3 9.3 8.4 20.1 8.4 31.6c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zm223.1 298L373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5z\"/></svg>" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: EyeSlashSolid, decorators: [{
            type: Component,
            args: [{ selector: 'eye-slash-solid', template: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 640 512\"><!--! Font Awesome Pro 6.2.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d=\"M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c5.2-11.8 8-24.8 8-38.5c0-53-43-96-96-96c-2.8 0-5.6 .1-8.4 .4c5.3 9.3 8.4 20.1 8.4 31.6c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zm223.1 298L373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5z\"/></svg>" }]
        }] });

class Warning {
    screenProvider;
    i18n;
    translate;
    companyName;
    companyLogo;
    widget;
    lang;
    container = container$1;
    globalStyle = globalStyle;
    imageContainer = imageContainer;
    checkbox = checkbox;
    image = image;
    svg = svg$3;
    text = pText;
    input = input$3;
    buttonContainer = buttonContainer$1;
    button = {
        padding: '0px 30px',
        backgroundColor: '#0f876d',
        color: '#ffffff',
        fontSize: 14,
        height: 40,
    };
    containerStyle;
    initStyle() {
        this.containerStyle = {
            width: 400,
        };
    }
    constructor(screenProvider, i18n, translate) {
        this.screenProvider = screenProvider;
        this.i18n = i18n;
        this.translate = translate;
        this.initStyle();
    }
    ngOnInit() {
        this.i18n.init(this.translate, this.lang);
    }
    ngOnChanges() {
        this.i18n.changeLanguage(this.lang);
    }
    goNextScreen() {
        if (this.widget == 'providers') {
            this.screenProvider.screenTransition('list');
        }
        else {
            this.screenProvider.screenTransition('upload');
        }
    }
    handleCheckboxChange(event) {
        const target = event.target;
        localStorage.setItem('dismissWarningScreen', target.checked.toString());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Warning, deps: [{ token: ScreenProvider }, { token: I18n }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Warning, selector: "warning", inputs: { companyName: "companyName", companyLogo: "companyLogo", widget: "widget", lang: "lang" }, usesOnChanges: true, ngImport: i0, template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <div [ngClass]=\"imageContainer\">\n    <img\n      [src]=\"companyLogo\"\n      [ngClass]=\"image({ width: 50, height: 50, margin: 15 })\"\n      alt=\"\"\n    />\n    <arrow-right-arrow-left-solid\n      [ngClass]=\"svg({ width: 20, height: 20 })\"\n      alt=\"arrows\"\n    />\n    <leaf-logo\n      [ngClass]=\"svg({ width: 50, height: 50, margin: '15px' })\"\n      alt=\"leaf-logo\"\n    ></leaf-logo>\n  </div>\n  <p\n    [ngClass]=\"\n      text({ fontSize: 22, margin: '40px 30px 0px 30px', textAlign: 'center' })\n    \"\n  >\n    {{ companyName }} {{ \"screens.warning.uses\" | translate }}\n    <span\n      [ngClass]=\"\n        text({\n          fontWeight: 'bold',\n        })\n      \"\n      >Leaf</span\n    >\n    {{ \"screens.warning.connectProviders\" | translate }}\n  </p>\n  <eye-slash-solid\n    [ngClass]=\"svg({ width: 20, height: 20, margin: '80px 0px 0px 50px' })\"\n    alt=\"eye-slash\"\n  />\n  <p\n    [ngClass]=\"\n      text({\n        fontSize: 18,\n        margin: '-22px 0px 0px 90px',\n        fontWeight: 'bold',\n        display: 'flex'\n      })\n    \"\n  >\n    {{ \"screens.warning.dataBelongsToYou\" | translate }}\n  </p>\n  <p\n    [ngClass]=\"\n      text({\n        fontSize: 16,\n        margin: '15px 60px 0px 90px',\n        display: 'flex'\n      })\n    \"\n  >\n    {{ \"screens.warning.leafDisclamer\" | translate }} {{ companyName }}\n  </p>\n  <label>\n    <input\n      (change)=\"handleCheckboxChange($event)\"\n      type=\"checkbox\"\n      [ngClass]=\"\n        input({\n          display: 'flex',\n          width: 17,\n          height: 15,\n          margin: '50px 0px 0px 50px',\n        })\n      \"\n    />\n    <p\n      [ngClass]=\"\n        text({\n          fontSize: 16,\n          margin: '-17px 0px 0px 80px',\n          display: 'flex',\n        })\n      \"\n    >\n      {{ \"screens.warning.notShowAgain\" | translate }} {{ companyName }}\n    </p>\n  </label>\n  <div [ngClass]=\"buttonContainer\">\n    <custom-button\n      text=\"{{ 'screens.warning.continue' | translate }}\"\n      [buttonStyle]=\"button\"\n      data-testid=\"warning-continue-button\"\n      (click)=\"goNextScreen()\"\n    ></custom-button>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: CustomButton, selector: "custom-button", inputs: ["text", "buttonStyle"] }, { kind: "component", type: LeafLogo, selector: "leaf-logo" }, { kind: "component", type: ArrowRightArrowLeftSolid, selector: "arrow-right-arrow-left-solid" }, { kind: "component", type: EyeSlashSolid, selector: "eye-slash-solid" }, { kind: "pipe", type: i1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Warning, decorators: [{
            type: Component,
            args: [{ selector: 'warning', template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <div [ngClass]=\"imageContainer\">\n    <img\n      [src]=\"companyLogo\"\n      [ngClass]=\"image({ width: 50, height: 50, margin: 15 })\"\n      alt=\"\"\n    />\n    <arrow-right-arrow-left-solid\n      [ngClass]=\"svg({ width: 20, height: 20 })\"\n      alt=\"arrows\"\n    />\n    <leaf-logo\n      [ngClass]=\"svg({ width: 50, height: 50, margin: '15px' })\"\n      alt=\"leaf-logo\"\n    ></leaf-logo>\n  </div>\n  <p\n    [ngClass]=\"\n      text({ fontSize: 22, margin: '40px 30px 0px 30px', textAlign: 'center' })\n    \"\n  >\n    {{ companyName }} {{ \"screens.warning.uses\" | translate }}\n    <span\n      [ngClass]=\"\n        text({\n          fontWeight: 'bold',\n        })\n      \"\n      >Leaf</span\n    >\n    {{ \"screens.warning.connectProviders\" | translate }}\n  </p>\n  <eye-slash-solid\n    [ngClass]=\"svg({ width: 20, height: 20, margin: '80px 0px 0px 50px' })\"\n    alt=\"eye-slash\"\n  />\n  <p\n    [ngClass]=\"\n      text({\n        fontSize: 18,\n        margin: '-22px 0px 0px 90px',\n        fontWeight: 'bold',\n        display: 'flex'\n      })\n    \"\n  >\n    {{ \"screens.warning.dataBelongsToYou\" | translate }}\n  </p>\n  <p\n    [ngClass]=\"\n      text({\n        fontSize: 16,\n        margin: '15px 60px 0px 90px',\n        display: 'flex'\n      })\n    \"\n  >\n    {{ \"screens.warning.leafDisclamer\" | translate }} {{ companyName }}\n  </p>\n  <label>\n    <input\n      (change)=\"handleCheckboxChange($event)\"\n      type=\"checkbox\"\n      [ngClass]=\"\n        input({\n          display: 'flex',\n          width: 17,\n          height: 15,\n          margin: '50px 0px 0px 50px',\n        })\n      \"\n    />\n    <p\n      [ngClass]=\"\n        text({\n          fontSize: 16,\n          margin: '-17px 0px 0px 80px',\n          display: 'flex',\n        })\n      \"\n    >\n      {{ \"screens.warning.notShowAgain\" | translate }} {{ companyName }}\n    </p>\n  </label>\n  <div [ngClass]=\"buttonContainer\">\n    <custom-button\n      text=\"{{ 'screens.warning.continue' | translate }}\"\n      [buttonStyle]=\"button\"\n      data-testid=\"warning-continue-button\"\n      (click)=\"goNextScreen()\"\n    ></custom-button>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: ScreenProvider }, { type: I18n }, { type: i1.TranslateService }]; }, propDecorators: { companyName: [{
                type: Input
            }], companyLogo: [{
                type: Input
            }], widget: [{
                type: Input
            }], lang: [{
                type: Input
            }] } });

const uploadedFilesBox = ({ historicalLenght, }) => css `
  width: 360px;
  height: 300px;
  margin-left: 20px;
  border-radius: 5px;
  box-shadow: ${historicalLenght ? 'none' : '0px 0px 6px #00000029'};
  overflow-x: hidden;
  overflow-y: auto;

  @media screen and (max-width: 640px) {
    width: 100%;
    margin-left: 0;
  }
`;
const input$2 = css ``;
const dropZoneDiv = css `
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 1rem;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  margin-bottom: -50px;
`;
const uploadedFilesText = css `
  color: #7e7e7e;
  margin-top: 150px;
  font-size: 16px;
  text-align: center;
  padding-left: 5px;
  padding-right: 5px;
`;
const baseboardContainer$3 = css `
    display: flex;
    justify-content: flex-end;
    margin: 0px 0px 0px 0px;
`;

var UploadStatus;
(function (UploadStatus) {
    UploadStatus["UPLOADING"] = "Uploading...";
    UploadStatus["PROGRESS"] = "Progress...";
    UploadStatus["RECEIVED"] = "RECEIVED";
    UploadStatus["FAILED"] = "FAILED";
    UploadStatus["PROCESSED"] = "PROCESSED";
})(UploadStatus || (UploadStatus = {}));

const TagValuesProps = {
    PROCESSED: {
        value: 'Processed',
        backgroundColor: '#007E50'
    },
    RECEIVED: {
        value: 'Received',
        backgroundColor: '#007E50'
    },
    ERROR: {
        value: 'Failed',
        backgroundColor: '#BB0202'
    },
    ORGANIZATION_ALLOWED: {
        value: 'Allowed',
        backgroundColor: '#FFFFFF',
        icon: 'https://d2bkrhd64jf4kw.cloudfront.net/assets/icons/black-check-solid.svg'
    },
    ORGANIZATION_NOT_ALLOWED: {
        value: 'Not Allowed',
        backgroundColor: '#FFFFFF'
    },
    INCOMPLETE: {
        value: 'INCOMPLETE',
        backgroundColor: '#f49c12'
    },
    UNAUTHENTICATED: {
        value: 'UNAUTHENTICATED',
        backgroundColor: '#bb0202'
    },
};
const conformString = (string) => {
    if (string.length > 15) {
        let conformed = string.substring(0, 15);
        conformed = `${conformed}...`;
        return conformed;
    }
    return string;
};
const getISOStringDatetime = (daysInterval) => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - daysInterval);
    return currentDate.toISOString();
};
const formatDateTime = (dateTime) => {
    const date = new Date(dateTime).toLocaleString('en-US');
    return date;
};
const leafProviders = {
    JOHN_DEERE: {
        provider: 'JohnDeere',
        providerName: 'Operations Center',
        providerUrl: 'https://operationscenter.deere.com',
        providerLogo: 'https://d2bkrhd64jf4kw.cloudfront.net/assets/jd-icon.png',
        providerOrder: 0,
        providerNameLowerCase: 'john-deere'
    },
    TRIMBLE: {
        provider: 'Trimble',
        providerName: 'Trimble',
        providerUrl: 'https://agriculture.trimble.com',
        providerLogo: 'https://d2bkrhd64jf4kw.cloudfront.net/assets/trimble.png',
        providerOrder: 3,
        providerNameLowerCase: 'trimble'
    },
    CLIMATE_FIELD_VIEW: {
        provider: 'ClimateFieldView',
        providerName: 'Climate FieldView',
        providerUrl: 'https://climate.com',
        providerLogo: 'https://d2bkrhd64jf4kw.cloudfront.net/assets/climate-icon.png',
        providerOrder: 1,
        providerNameLowerCase: 'climate-field-view'
    },
    AGLEADER: {
        provider: 'AgLeader',
        providerName: 'Ag Leader',
        providerUrl: 'https://www.agleader.com',
        providerLogo: 'https://d2bkrhd64jf4kw.cloudfront.net/assets/ag-leader-icon.png',
        providerOrder: 4,
        providerNameLowerCase: 'ag-leader'
    },
    CNHI: {
        provider: 'CNHI',
        providerName: 'AFS Connect',
        providerUrl: 'https://www.caseih.com',
        providerLogo: 'https://d2bkrhd64jf4kw.cloudfront.net/assets/cnhi-icons.jpeg',
        providerOrder: 2,
        providerNameLowerCase: 'cnhi'
    }
};
function getLeafProviderInfo(provider) {
    const providerKey = Object.keys(leafProviders).find(key => leafProviders[key].provider === provider);
    return leafProviders[(providerKey ?? '')];
}
const doesNotContainsProviderNameInList = (providersList, name) => {
    const found = providersList.find((p) => p.provider === name);
    return found ? false : true;
};

const environment = {
    production: true,
    APP_BASE_URL: 'https://api.withleaf.io/',
    INTERVAL_IN_MILLIS: 5000,
    TIMEOUT_IN_MILLIS: 90000,
};

const api = axios.create();
api.interceptors.request.use((config) => {
    config.baseURL = environment.APP_BASE_URL;
    config.responseType = 'json';
    return config;
});
const intercept = (apiKey) => {
    if (apiKey) {
        api.interceptors.request.use((config) => {
            config.headers['x-api-key'] = apiKey;
            return config;
        });
    }
    else {
        throw new Error('apiKey not found');
    }
};

class OperationsService {
    async leafGetBatch(apiKey, leafUser, uploadStartTime) {
        intercept(apiKey);
        const { data } = await api.get(`services/operations/api/batch?leafUserId=${leafUser}&uploadStartTime=${uploadStartTime}&page=0&size=100&sort=uploadTimestamp`);
        return data;
    }
    ;
    async leafFileUpload(leafUser, apiKey, file) {
        intercept(apiKey);
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post(`services/operations/api/batch?leafUserId=${leafUser}&provider=Other`, formData);
        return data;
    }
    ;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OperationsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OperationsService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OperationsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class LeafProviderData {
    providersConnected;
    providerWidgetStatus;
    leafBatchIds;
    constructor() {
        this.providersConnected = [];
        this.providerWidgetStatus = {
            code: 0,
            message: 'Started'
        };
        this.leafBatchIds = [];
    }
    setProvidersConnected(value) {
        this.providersConnected.push(value);
    }
    setProviderWidgetStatus(status) {
        this.providerWidgetStatus = status;
    }
    addLeafBatchId(value) {
        this.leafBatchIds.push(value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafProviderData, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafProviderData, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafProviderData, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

const dashedBorder$1 = css `
  border: 1px dashed #eeeaf2;
  border-radius: 5px;
  margin-bottom: 0px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (max-width: 640px) {
    width: calc(100% - 8px);
  }
`;
const svg$2 = ({ width, height, margin }) => css `
  width: ${width}px;
  height: ${height}px;
  margin: ${margin};
`;
const browserOrDropBox = css `
  background-color: #ffffff;
  width: 360px;
  margin-left: 20px;
  border-radius: 5px;
  cursor: pointer;

  @media screen and (max-width: 640px) {
    width: 100%;
    margin-left: 0;
  }
`;
const input$1 = css ``;

class FilesDuotone {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FilesDuotone, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: FilesDuotone, selector: "files-duotone", ngImport: i0, template: "    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'>\n        <path\n            fill='#000000'\n            d='M64 0C28.7 0 0 28.7 0 64v384c0 35.3 28.7 64 64 64h256c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zm192 0v128h128L256 0zM96 48c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16h-32c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16h-32c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16h-32c-8.8 0-16-7.2-16-16zm-6.3 71.8c3.7-14 16.4-23.8 30.9-23.8h14.8c14.5 0 27.2 9.7 30.9 23.8l23.5 88.2c1.4 5.4 2.1 10.9 2.1 16.4 0 35.2-28.8 63.7-64 63.7s-64-28.5-64-63.7c0-5.5.7-11.1 2.1-16.4l23.5-88.2zM112 336c-8.8 0-16 7.2-16 16s7.2 16 16 16h32c8.8 0 16-7.2 16-16s-7.2-16-16-16h-32z'\n            className='color000 svgShape'\n        />\n    </svg>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FilesDuotone, decorators: [{
            type: Component,
            args: [{ selector: 'files-duotone', template: "    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'>\n        <path\n            fill='#000000'\n            d='M64 0C28.7 0 0 28.7 0 64v384c0 35.3 28.7 64 64 64h256c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zm192 0v128h128L256 0zM96 48c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16h-32c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16h-32c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16h-32c-8.8 0-16-7.2-16-16zm-6.3 71.8c3.7-14 16.4-23.8 30.9-23.8h14.8c14.5 0 27.2 9.7 30.9 23.8l23.5 88.2c1.4 5.4 2.1 10.9 2.1 16.4 0 35.2-28.8 63.7-64 63.7s-64-28.5-64-63.7c0-5.5.7-11.1 2.1-16.4l23.5-88.2zM112 336c-8.8 0-16 7.2-16 16s7.2 16 16 16h32c8.8 0 16-7.2 16-16s-7.2-16-16-16h-32z'\n            className='color000 svgShape'\n        />\n    </svg>\n" }]
        }] });

class DragAndDrop {
    i18n;
    translate;
    apiKey;
    leafUser;
    historicalFiles;
    lang;
    updateAndSendEvent = new EventEmitter();
    input = input$1;
    browserOrDropBox = browserOrDropBox;
    dashedBorder = dashedBorder$1;
    svg = svg$2;
    text = simpleText;
    fileZipperSolidWhiteStyle = {
        width: 20,
        height: 25,
        margin: '5px 0px 5px 0px',
    };
    browseTextStyle = {
        fontSize: 16,
        margin: '5px 0px 0px 0px',
    };
    zipTextStyle = {
        fontSize: 10,
        margin: '5px 0px 15px 0px',
    };
    async handleChange(event) {
        event.preventDefault();
        const target = event.target;
        if (target.files !== null) {
            this.upadteAndSendEmitter(Array.from(target.files));
        }
    }
    constructor(i18n, translate) {
        this.i18n = i18n;
        this.translate = translate;
    }
    ngOnInit() {
        this.i18n.init(this.translate, this.lang);
    }
    ngOnChanges() {
        this.i18n.changeLanguage(this.lang);
    }
    upadteAndSendEmitter(files) {
        this.updateAndSendEvent.emit(files);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: DragAndDrop, deps: [{ token: I18n }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: DragAndDrop, selector: "drag-and-drop", inputs: { apiKey: "apiKey", leafUser: "leafUser", historicalFiles: "historicalFiles", lang: "lang" }, outputs: { updateAndSendEvent: "updateAndSendEvent" }, usesOnChanges: true, ngImport: i0, template: "<input\n  #fileUpload\n  multiple\n  hidden\n  type=\"file\"\n  accept=\".zip\"\n  [ngClass]=\"input\"\n  (change)=\"handleChange($event)\"\n  data-testid=\"input-files\"\n/>\n<label data-testid=\"label-element\" (click)=\"fileUpload.click()\">\n  <div [ngClass]=\"browserOrDropBox\">\n    <div [ngClass]=\"dashedBorder\">\n      <files-duotone\n        [ngClass]=\"svg(fileZipperSolidWhiteStyle)\"\n        alt=\"file-zipper-solid-white\"\n      />\n      <p [ngClass]=\"text(browseTextStyle)\" data-testid=\"browse-text\">\n        {{ \"screens.fileUpload.dragAndDrop\" | translate }}\n      </p>\n      <p [ngClass]=\"text(zipTextStyle)\" data-testid=\"zip-text\">\n        {{ \"screens.fileUpload.zipOnly\" | translate }}\n      </p>\n    </div>\n  </div>\n</label>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: FilesDuotone, selector: "files-duotone" }, { kind: "pipe", type: i1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: DragAndDrop, decorators: [{
            type: Component,
            args: [{ selector: 'drag-and-drop', template: "<input\n  #fileUpload\n  multiple\n  hidden\n  type=\"file\"\n  accept=\".zip\"\n  [ngClass]=\"input\"\n  (change)=\"handleChange($event)\"\n  data-testid=\"input-files\"\n/>\n<label data-testid=\"label-element\" (click)=\"fileUpload.click()\">\n  <div [ngClass]=\"browserOrDropBox\">\n    <div [ngClass]=\"dashedBorder\">\n      <files-duotone\n        [ngClass]=\"svg(fileZipperSolidWhiteStyle)\"\n        alt=\"file-zipper-solid-white\"\n      />\n      <p [ngClass]=\"text(browseTextStyle)\" data-testid=\"browse-text\">\n        {{ \"screens.fileUpload.dragAndDrop\" | translate }}\n      </p>\n      <p [ngClass]=\"text(zipTextStyle)\" data-testid=\"zip-text\">\n        {{ \"screens.fileUpload.zipOnly\" | translate }}\n      </p>\n    </div>\n  </div>\n</label>\n" }]
        }], ctorParameters: function () { return [{ type: I18n }, { type: i1.TranslateService }]; }, propDecorators: { apiKey: [{
                type: Input
            }], leafUser: [{
                type: Input
            }], historicalFiles: [{
                type: Input
            }], lang: [{
                type: Input
            }], updateAndSendEvent: [{
                type: Output
            }] } });

const errorContainer = css `
  width: 100%;
  padding: 8px 16px;
  box-sizing: border-box;
  background-color: #9a0000;
  position: absolute;
`;
const svg$1 = ({ width, height, margin }) => css `
  width: ${width}px;
  height: ${height}px;
  margin: ${margin};
`;
const text = css `
  font-size: 14px;
  color: white;
`;
const errorDiv = css `
  display: flex;
  gap: 16px;
  align-items: center;
`;
const errorButton = css `
  padding: 8px 16px;
  border-radius: 50px;
  background-color: #9a0000;
  border: 1px solid #ae3333;
  cursor: pointer;
  color: white;
  float: right;
`;

class WarningIcon {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: WarningIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: WarningIcon, selector: "warning-icon", ngImport: i0, template: "\n    <svg\n        xmlns='http://www.w3.org/2000/svg'\n        fill='none'\n        viewBox='0 0 500 500'\n    >\n        <path\n            fill='#fff'\n            fillRule='evenodd'\n            d='M253.99 7.5c-5.773-10-20.207-10-25.98 0L2.843 397.5c-5.773 10 1.443 22.5 12.99 22.5h450.334c11.547 0 18.763-12.5 12.99-22.5L253.99 7.5ZM211.634 97l2.831 205.705h46.07L263.367 97h-51.733Zm10.015 277.776.024.014c4.809 2.848 10.153 4.21 15.827 4.21 8.56 0 16.101-3.116 22.201-9.137 6.102-6.024 9.299-13.517 9.299-22.061s-3.197-16.036-9.299-22.06c-6.1-6.021-13.641-9.137-22.201-9.137-8.559 0-16.101 3.116-22.201 9.137-6.102 6.024-9.299 13.516-9.299 22.06 0 5.645 1.475 10.95 4.42 15.742 2.772 4.652 6.543 8.411 11.205 11.217l.024.015Z'\n            clipRule='evenodd'\n        />\n    </svg>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: WarningIcon, decorators: [{
            type: Component,
            args: [{ selector: 'warning-icon', template: "\n    <svg\n        xmlns='http://www.w3.org/2000/svg'\n        fill='none'\n        viewBox='0 0 500 500'\n    >\n        <path\n            fill='#fff'\n            fillRule='evenodd'\n            d='M253.99 7.5c-5.773-10-20.207-10-25.98 0L2.843 397.5c-5.773 10 1.443 22.5 12.99 22.5h450.334c11.547 0 18.763-12.5 12.99-22.5L253.99 7.5ZM211.634 97l2.831 205.705h46.07L263.367 97h-51.733Zm10.015 277.776.024.014c4.809 2.848 10.153 4.21 15.827 4.21 8.56 0 16.101-3.116 22.201-9.137 6.102-6.024 9.299-13.517 9.299-22.061s-3.197-16.036-9.299-22.06c-6.1-6.021-13.641-9.137-22.201-9.137-8.559 0-16.101 3.116-22.201 9.137-6.102 6.024-9.299 13.516-9.299 22.06 0 5.645 1.475 10.95 4.42 15.742 2.772 4.652 6.543 8.411 11.205 11.217l.024.015Z'\n            clipRule='evenodd'\n        />\n    </svg>\n" }]
        }] });

class ErrorMessage {
    errorMessage;
    okClick;
    errorContainer = errorContainer;
    svg = svg$1;
    text = text;
    errorDiv = errorDiv;
    errorButton = errorButton;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ErrorMessage, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: ErrorMessage, selector: "error-message", inputs: { errorMessage: "errorMessage", okClick: "okClick" }, ngImport: i0, template: "<div [ngClass]=\"errorContainer\">\n  <div [ngClass]=\"errorDiv\">\n    <warning-icon [ngClass]=\"svg({ width: 20, height: 25 })\" alt=\"icon-image\" data-testid=\"error-icon-image\" />\n    <p [ngClass]=\"text\" data-testid=\"error-browse-text\">\n      {{ errorMessage }}\n    </p>\n  </div>\n  <button\n    [ngClass]=\"errorButton\"\n    (click)=\"(okClick)\"\n    data-testid=\"error-ok-button\"\n  >\n    Ok\n  </button>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: WarningIcon, selector: "warning-icon" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ErrorMessage, decorators: [{
            type: Component,
            args: [{ selector: 'error-message', template: "<div [ngClass]=\"errorContainer\">\n  <div [ngClass]=\"errorDiv\">\n    <warning-icon [ngClass]=\"svg({ width: 20, height: 25 })\" alt=\"icon-image\" data-testid=\"error-icon-image\" />\n    <p [ngClass]=\"text\" data-testid=\"error-browse-text\">\n      {{ errorMessage }}\n    </p>\n  </div>\n  <button\n    [ngClass]=\"errorButton\"\n    (click)=\"(okClick)\"\n    data-testid=\"error-ok-button\"\n  >\n    Ok\n  </button>\n</div>\n" }]
        }], propDecorators: { errorMessage: [{
                type: Input
            }], okClick: [{
                type: Input
            }] } });
;

const container = css `
  position: absolute;
  background-color: white;
  width: 400px;
  height: 100%;
  margin-top: -25px;
  background: rgba(255, 255, 255, 0.8);

  @media screen and (max-width: 640px) {
    width: 100%;
  }
`;
const dashedBorder = css `
  display: flex;
  border: 1px dashed #464646;
  height: 98%;
  margin: 5px 5px 5px 5px;
  align-items: center;
  justify-content: center;
`;
const imageAndTextContainer = css `
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const fileUploadOverlayImage = css `
  width: 100px;
  height: 100px;
  margin-bottom: 25px;
`;
const fileUploadOverlayText = css `
  font-size: 20px;
  color: black;
  text-align: center;
`;

class FileArrowUpRegular {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileArrowUpRegular, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: FileArrowUpRegular, selector: "file-arrow-up-regular", ngImport: i0, template: "\n    <svg\n        xmlns='http://www.w3.org/2000/svg'\n        fill='none'\n        viewBox='0 0 384 512'\n    >\n        <g clipPath='url(#a)'>\n            <path\n                fill='#464646'\n                d='M48 448V64c0-8.8 7.2-16 16-16h160v80c0 17.7 14.3 32 32 32h80v288c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16ZM64 0C28.7 0 0 28.7 0 64v384c0 35.3 28.7 64 64 64h256c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3l-90.6-90.5C262.7 6.7 246.5 0 229.5 0H64Zm152 408V305.9l31 31c9.4 9.4 24.6 9.4 33.9 0 9.3-9.4 9.4-24.6 0-33.9l-72-72c-9.4-9.4-24.6-9.4-33.9 0l-72 72c-9.4 9.4-9.4 24.6 0 33.9 9.4 9.3 24.6 9.4 33.9 0l31-31V408c0 13.3 10.7 24 24 24s24-10.7 24-24h.1Z'\n            />\n        </g>\n        <defs>\n            <clipPath id='a'>\n                <path fill='#fff' d='M0 0h384v512H0z' />\n            </clipPath>\n        </defs>\n    </svg>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileArrowUpRegular, decorators: [{
            type: Component,
            args: [{ selector: 'file-arrow-up-regular', template: "\n    <svg\n        xmlns='http://www.w3.org/2000/svg'\n        fill='none'\n        viewBox='0 0 384 512'\n    >\n        <g clipPath='url(#a)'>\n            <path\n                fill='#464646'\n                d='M48 448V64c0-8.8 7.2-16 16-16h160v80c0 17.7 14.3 32 32 32h80v288c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16ZM64 0C28.7 0 0 28.7 0 64v384c0 35.3 28.7 64 64 64h256c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3l-90.6-90.5C262.7 6.7 246.5 0 229.5 0H64Zm152 408V305.9l31 31c9.4 9.4 24.6 9.4 33.9 0 9.3-9.4 9.4-24.6 0-33.9l-72-72c-9.4-9.4-24.6-9.4-33.9 0l-72 72c-9.4 9.4-9.4 24.6 0 33.9 9.4 9.3 24.6 9.4 33.9 0l31-31V408c0 13.3 10.7 24 24 24s24-10.7 24-24h.1Z'\n            />\n        </g>\n        <defs>\n            <clipPath id='a'>\n                <path fill='#fff' d='M0 0h384v512H0z' />\n            </clipPath>\n        </defs>\n    </svg>\n" }]
        }] });

class FileUploadOverlay {
    container = container;
    dashedBorder = dashedBorder;
    imageAndTextContainer = imageAndTextContainer;
    fileUploadOverlayImage = fileUploadOverlayImage;
    fileUploadOverlayText = fileUploadOverlayText;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUploadOverlay, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: FileUploadOverlay, selector: "file-upload-overlay", ngImport: i0, template: "<div [ngClass]=\"container\">\n\t<div [ngClass]=\"dashedBorder\" data-testid=\"dashed-border\">\n\t\t<div [ngClass]=\"imageAndTextContainer\">\n      <file-arrow-up-regular [ngClass]=\"fileUploadOverlayImage\" data-testid=\"file-upload-overlay-image\" alt=\"file-overlay-image\" />\n\t\t\t<p [ngClass]=\"fileUploadOverlayText\" data-testid=\"file-upload-overlay-text\">\n\t\t\t\tDrop your files here to upload\n\t\t\t</p>\n\t\t</div>\n\t</div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: FileArrowUpRegular, selector: "file-arrow-up-regular" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUploadOverlay, decorators: [{
            type: Component,
            args: [{ selector: 'file-upload-overlay', template: "<div [ngClass]=\"container\">\n\t<div [ngClass]=\"dashedBorder\" data-testid=\"dashed-border\">\n\t\t<div [ngClass]=\"imageAndTextContainer\">\n      <file-arrow-up-regular [ngClass]=\"fileUploadOverlayImage\" data-testid=\"file-upload-overlay-image\" alt=\"file-overlay-image\" />\n\t\t\t<p [ngClass]=\"fileUploadOverlayText\" data-testid=\"file-upload-overlay-text\">\n\t\t\t\tDrop your files here to upload\n\t\t\t</p>\n\t\t</div>\n\t</div>\n</div>\n" }]
        }] });
;

const svg = () => css `
  display: flex;
  width: 3.75rem;
  height: auto;
`;
const poweredByDiv = () => css `
  display: flex;
  gap: 0.5rem;
  float: right;
  margin-top: 3rem;
  margin-right: 1.25rem;
  align-items: center;
`;

class LeafLogoWithText {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafLogoWithText, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: LeafLogoWithText, selector: "leaf-logo-with-text", ngImport: i0, template: "<svg\n\nxmlns=\"http://www.w3.org/2000/svg\"\n\nversion=\"1.1\"\n\nviewBox=\"0 0 442.04 152.04\"\n\nxmlSpace=\"preserve\"\n\n>\n\n<g transform=\"matrix(1.33333 0 0 -1.33333 0 152.04)\">\n\n  <g\n\n    fillOpacity=\"1\"\n\n    fillRule=\"nonzero\"\n\n    stroke=\"none\"\n\n    transform=\"scale(.1)\"\n\n  >\n\n    <path\n\n      fill=\"#183f34\"\n\n      d=\"M116.852 623.223v392.547a7.71 7.71 0 007.714 7.71h278.825a7.713 7.713 0 017.714 7.72v51.41c0 4.27-3.453 7.72-7.714 7.72H57.715a7.713 7.713 0 01-7.715-7.72V623.223a7.711 7.711 0 017.715-7.715h51.422a7.712 7.712 0 017.715 7.715\"\n\n    ></path>\n\n    <path\n\n      fill=\"#2d876a\"\n\n      d=\"M957.988 63.172l-51.425 51.418a7.71 7.71 0 01-5.454 2.262H603.691v.207h-33.906c-235.676.14-429.847 181.097-451.019 411.375-.368 3.992-3.668 7.062-7.672 7.062h-51.59c-4.492 0-8.074-3.828-7.707-8.301C72.87 271.449 279.789 67.551 536.84 51.25V50h415.695c6.867 0 10.313 8.309 5.453 13.172\"\n\n    ></path>\n\n    <path\n\n      fill=\"#2d876a\"\n\n      d=\"M1023.27 570.445v-.156l.21-36.055-1.15-419.785v2.403L1089.18 50h1.15v553.488h-1.25c-17.18 271.36-243.4 486.842-519.017 486.842h-71.235a7.715 7.715 0 01-7.715-7.72v-51.41c0-4.27 3.457-7.72 7.715-7.72h71.235c249.839 0 453.107-203.218 453.207-453.035\"\n\n    ></path>\n\n    <path\n\n      fill=\"#183f34\"\n\n      d=\"M722.645 409.969h-140.77c-3.895.136-7.797.207-11.73.207H569.73c-88.093.168-159.757 71.984-159.757 160.09v152.379c0 4.257 3.449 7.714 7.707 7.714h152.383c88.046 0 159.863-71.621 160.093-159.48l-.004-.816c0-3.864.067-7.79.207-11.68v-54.805l-86.531 86.531a7.71 7.71 0 01-5.453 2.262h-72.73c-6.872 0-10.313-8.312-5.454-13.168L727.543 411.77c-1.332-1.11-3.027-1.801-4.898-1.801zm299.925-198.594l-78.8 78.793v272.023l-.043 1.762c-.11 2.027-.164 4.082-.164 6.11v.816c-.325 205.613-167.879 372.887-373.5 372.887v-66.848c168.82 0 306.382-137.34 306.652-305.93v-.925c0-2.887.066-5.813.203-8.708V357.02l-79.707 79.707v122.261l-.023 1.25c-.122 3.27-.184 6.575-.184 9.739l.004.902c-.324 124.797-102.129 226.332-226.945 226.332H350.828a7.716 7.716 0 01-7.715-7.715v-219.23c0-124.891 101.606-226.7 226.692-226.95l.465.008c3.187 0 6.476-.062 9.773-.187l209.453-.02c1.867 0 3.551.692 4.883 1.789l81.457-81.496h-.07l66.855-66.855h.035l66.554-66.586 13.17-13.117.2 91.289c0 2.058-.01 3.234-.01 3.234\"\n\n    ></path>\n\n    <path\n\n      fill=\"#183f34\"\n\n      d=\"M760.004 261.148a7.694 7.694 0 01-5.453 2.262H578.922a188.92 188.92 0 01-8.656.207l-.711-.008c-168.809.27-306.149 137.84-306.149 306.657v139.246a7.708 7.708 0 01-7.707 7.707h-51.426c-4.257 0-7.714-3.453-7.714-7.707V570.266c0-205.625 167.281-373.172 372.89-373.504l.817.004a117.1 117.1 0 006.078-.161l229.613-.031c6.875 0 10.316 8.313 5.453 13.168l-51.406 51.406\"\n\n    ></path>\n\n    <path\n\n      fill=\"#2d876a\"\n\n      d=\"M570.063 876.918H271.121a7.716 7.716 0 01-7.715-7.715v-64.254c0-4.261-3.449-7.715-7.707-7.715h-51.426a7.717 7.717 0 00-7.714 7.715v131.106a7.713 7.713 0 007.714 7.711h365.79v-66.848\"\n\n    ></path>\n\n    <path\n\n      fill=\"#100f0d\"\n\n      d=\"M1528.8 425.883v505.695c0 6.789-5.5 12.293-12.29 12.293h-105.99V325.953c0-64.758 52.5-129.496 117.26-129.496h233.53v110.121h-151.95c-80.77 0-80.56 47.348-80.56 119.305\"\n\n    ></path>\n\n    <path\n\n      fill=\"#100f0d\"\n\n      d=\"M2634.49 306.578c-77.5 0-135.61 64.246-135.61 140.719 0 76.476 58.11 139.687 134.59 139.687 70.36 0 137.65-58.113 137.65-139.687 0-79.539-63.21-140.719-136.63-140.719zm138.67 312.024c-31.61 49.961-94.82 78.515-160.08 78.515-134.6 0-232.48-106.051-232.48-249.82 0-147.856 101.96-250.84 229.42-250.84 68.32 0 131.53 34.668 163.14 86.672v-86.672h118.29v500.66h-118.29v-78.515\"\n\n    ></path>\n\n    <path\n\n      fill=\"#100f0d\"\n\n      d=\"M3232.65 833.746h32.62v110.125h-56.08c-132.56 0-197.82-82.594-197.82-219.226V196.457h118.29v406.852h106.04v93.808h-106.04v33.645c-1.03 58.117 22.43 102.984 102.99 102.984\"\n\n    ></path>\n\n    <path\n\n      fill=\"#100f0d\"\n\n      d=\"M1942.19 488.082c14.27 72.395 66.28 104.008 129.5 104.008 66.27 0 117.26-38.746 124.39-104.008zm371.15-36.711c0 148.871-107.06 245.746-242.68 245.746-136.63 0-249.82-97.89-249.82-249.82 0-148.875 108.1-250.84 249.82-250.84 86.67 0 171.3 38.75 215.15 108.082 15.31 22.887 25.55 60.16 25.55 60.16h-105.08c-27.53-38.746-77.49-61.176-128.48-61.176-71.37 0-122.36 35.688-135.61 101.961h369.12c1.01 17.34 2.03 32.629 2.03 45.887\"\n\n    ></path>\n\n  </g>\n\n</g>\n\n</svg>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafLogoWithText, decorators: [{
            type: Component,
            args: [{ selector: 'leaf-logo-with-text', template: "<svg\n\nxmlns=\"http://www.w3.org/2000/svg\"\n\nversion=\"1.1\"\n\nviewBox=\"0 0 442.04 152.04\"\n\nxmlSpace=\"preserve\"\n\n>\n\n<g transform=\"matrix(1.33333 0 0 -1.33333 0 152.04)\">\n\n  <g\n\n    fillOpacity=\"1\"\n\n    fillRule=\"nonzero\"\n\n    stroke=\"none\"\n\n    transform=\"scale(.1)\"\n\n  >\n\n    <path\n\n      fill=\"#183f34\"\n\n      d=\"M116.852 623.223v392.547a7.71 7.71 0 007.714 7.71h278.825a7.713 7.713 0 017.714 7.72v51.41c0 4.27-3.453 7.72-7.714 7.72H57.715a7.713 7.713 0 01-7.715-7.72V623.223a7.711 7.711 0 017.715-7.715h51.422a7.712 7.712 0 017.715 7.715\"\n\n    ></path>\n\n    <path\n\n      fill=\"#2d876a\"\n\n      d=\"M957.988 63.172l-51.425 51.418a7.71 7.71 0 01-5.454 2.262H603.691v.207h-33.906c-235.676.14-429.847 181.097-451.019 411.375-.368 3.992-3.668 7.062-7.672 7.062h-51.59c-4.492 0-8.074-3.828-7.707-8.301C72.87 271.449 279.789 67.551 536.84 51.25V50h415.695c6.867 0 10.313 8.309 5.453 13.172\"\n\n    ></path>\n\n    <path\n\n      fill=\"#2d876a\"\n\n      d=\"M1023.27 570.445v-.156l.21-36.055-1.15-419.785v2.403L1089.18 50h1.15v553.488h-1.25c-17.18 271.36-243.4 486.842-519.017 486.842h-71.235a7.715 7.715 0 01-7.715-7.72v-51.41c0-4.27 3.457-7.72 7.715-7.72h71.235c249.839 0 453.107-203.218 453.207-453.035\"\n\n    ></path>\n\n    <path\n\n      fill=\"#183f34\"\n\n      d=\"M722.645 409.969h-140.77c-3.895.136-7.797.207-11.73.207H569.73c-88.093.168-159.757 71.984-159.757 160.09v152.379c0 4.257 3.449 7.714 7.707 7.714h152.383c88.046 0 159.863-71.621 160.093-159.48l-.004-.816c0-3.864.067-7.79.207-11.68v-54.805l-86.531 86.531a7.71 7.71 0 01-5.453 2.262h-72.73c-6.872 0-10.313-8.312-5.454-13.168L727.543 411.77c-1.332-1.11-3.027-1.801-4.898-1.801zm299.925-198.594l-78.8 78.793v272.023l-.043 1.762c-.11 2.027-.164 4.082-.164 6.11v.816c-.325 205.613-167.879 372.887-373.5 372.887v-66.848c168.82 0 306.382-137.34 306.652-305.93v-.925c0-2.887.066-5.813.203-8.708V357.02l-79.707 79.707v122.261l-.023 1.25c-.122 3.27-.184 6.575-.184 9.739l.004.902c-.324 124.797-102.129 226.332-226.945 226.332H350.828a7.716 7.716 0 01-7.715-7.715v-219.23c0-124.891 101.606-226.7 226.692-226.95l.465.008c3.187 0 6.476-.062 9.773-.187l209.453-.02c1.867 0 3.551.692 4.883 1.789l81.457-81.496h-.07l66.855-66.855h.035l66.554-66.586 13.17-13.117.2 91.289c0 2.058-.01 3.234-.01 3.234\"\n\n    ></path>\n\n    <path\n\n      fill=\"#183f34\"\n\n      d=\"M760.004 261.148a7.694 7.694 0 01-5.453 2.262H578.922a188.92 188.92 0 01-8.656.207l-.711-.008c-168.809.27-306.149 137.84-306.149 306.657v139.246a7.708 7.708 0 01-7.707 7.707h-51.426c-4.257 0-7.714-3.453-7.714-7.707V570.266c0-205.625 167.281-373.172 372.89-373.504l.817.004a117.1 117.1 0 006.078-.161l229.613-.031c6.875 0 10.316 8.313 5.453 13.168l-51.406 51.406\"\n\n    ></path>\n\n    <path\n\n      fill=\"#2d876a\"\n\n      d=\"M570.063 876.918H271.121a7.716 7.716 0 01-7.715-7.715v-64.254c0-4.261-3.449-7.715-7.707-7.715h-51.426a7.717 7.717 0 00-7.714 7.715v131.106a7.713 7.713 0 007.714 7.711h365.79v-66.848\"\n\n    ></path>\n\n    <path\n\n      fill=\"#100f0d\"\n\n      d=\"M1528.8 425.883v505.695c0 6.789-5.5 12.293-12.29 12.293h-105.99V325.953c0-64.758 52.5-129.496 117.26-129.496h233.53v110.121h-151.95c-80.77 0-80.56 47.348-80.56 119.305\"\n\n    ></path>\n\n    <path\n\n      fill=\"#100f0d\"\n\n      d=\"M2634.49 306.578c-77.5 0-135.61 64.246-135.61 140.719 0 76.476 58.11 139.687 134.59 139.687 70.36 0 137.65-58.113 137.65-139.687 0-79.539-63.21-140.719-136.63-140.719zm138.67 312.024c-31.61 49.961-94.82 78.515-160.08 78.515-134.6 0-232.48-106.051-232.48-249.82 0-147.856 101.96-250.84 229.42-250.84 68.32 0 131.53 34.668 163.14 86.672v-86.672h118.29v500.66h-118.29v-78.515\"\n\n    ></path>\n\n    <path\n\n      fill=\"#100f0d\"\n\n      d=\"M3232.65 833.746h32.62v110.125h-56.08c-132.56 0-197.82-82.594-197.82-219.226V196.457h118.29v406.852h106.04v93.808h-106.04v33.645c-1.03 58.117 22.43 102.984 102.99 102.984\"\n\n    ></path>\n\n    <path\n\n      fill=\"#100f0d\"\n\n      d=\"M1942.19 488.082c14.27 72.395 66.28 104.008 129.5 104.008 66.27 0 117.26-38.746 124.39-104.008zm371.15-36.711c0 148.871-107.06 245.746-242.68 245.746-136.63 0-249.82-97.89-249.82-249.82 0-148.875 108.1-250.84 249.82-250.84 86.67 0 171.3 38.75 215.15 108.082 15.31 22.887 25.55 60.16 25.55 60.16h-105.08c-27.53-38.746-77.49-61.176-128.48-61.176-71.37 0-122.36 35.688-135.61 101.961h369.12c1.01 17.34 2.03 32.629 2.03 45.887\"\n\n    ></path>\n\n  </g>\n\n</g>\n\n</svg>\n" }]
        }] });

class Baseboard {
    text = simpleText;
    svg = svg;
    poweredByDiv = poweredByDiv;
    poweredByTextStyle = {
        fontSize: 10,
        color: '#7e7e7e',
    };
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Baseboard, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Baseboard, selector: "baseboard", ngImport: i0, template: "<div [ngClass]=\"poweredByDiv()\">\n  <p [ngClass]=\"text(poweredByTextStyle)\" data-testid=\"powered-by-text\">\n    Powered by\n  </p>\n  <leaf-logo-with-text [ngClass]=\"svg()\" data-testid=\"leaf-logo-with-text\" alt=\"leaf-logo\" />\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: LeafLogoWithText, selector: "leaf-logo-with-text" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Baseboard, decorators: [{
            type: Component,
            args: [{ selector: 'baseboard', template: "<div [ngClass]=\"poweredByDiv()\">\n  <p [ngClass]=\"text(poweredByTextStyle)\" data-testid=\"powered-by-text\">\n    Powered by\n  </p>\n  <leaf-logo-with-text [ngClass]=\"svg()\" data-testid=\"leaf-logo-with-text\" alt=\"leaf-logo\" />\n</div>\n" }]
        }] });

const tagContainer$1 = (props) => css `
    display: flex;
    justify-content: center;
    color: #FFFFFF;
    width: fit-content;
    min-width: 80px;
    height: fit-content;
    padding: 4px 12px 4px 12px;
    border-radius: 5px;
    font-weight: lighter;
    font-size: 14px;
    background-color: ${props?.backgroundColor};

    ${(props === TagValuesProps.ORGANIZATION_ALLOWED || props === TagValuesProps.ORGANIZATION_NOT_ALLOWED) &&
    `
            border: 1px solid #7E7E7E;
            color: #111111;
            padding: 2px 10px 2px 10px;
        `}
    ${(props === TagValuesProps.INCOMPLETE || props === TagValuesProps.UNAUTHENTICATED) &&
    `
            font-weight: bold;
            font-size: 10px;
        `}
`;
const tagIcon = css `
    width: 13px;
    margin-right: 5px;
`;

class Tag {
    props;
    tagContainer = tagContainer$1;
    tagIcon = tagIcon;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Tag, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Tag, selector: "tag", inputs: { props: "props" }, ngImport: i0, template: "<section [ngClass]=\"tagContainer(props)\">\n   <img [ngClass]=\"tagIcon\" *ngIf=\"props?.icon\" [src]=\"props?.icon\" alt=\"allowed-icon\">\n   {{props?.value}}\n</section>", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Tag, decorators: [{
            type: Component,
            args: [{ selector: 'tag', template: "<section [ngClass]=\"tagContainer(props)\">\n   <img [ngClass]=\"tagIcon\" *ngIf=\"props?.icon\" [src]=\"props?.icon\" alt=\"allowed-icon\">\n   {{props?.value}}\n</section>" }]
        }], propDecorators: { props: [{
                type: Input
            }] } });

class UploadedFileCard {
    dateTime = '';
    fileName;
    statusProps = { value: '', backgroundColor: '' };
    formatDateTime(date) {
        return formatDateTime(date);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UploadedFileCard, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: UploadedFileCard, selector: "uploaded-file-card", inputs: { dateTime: "dateTime", fileName: "fileName", statusProps: "statusProps" }, ngImport: i0, template: "<section class=\"upload-card-container\">\n    <section class=\"uploaded-card-data\">\n        <p class=\"uploaded-card-date\">\n            {{ formatDateTime(dateTime) }}\n        </p>\n        <p class=\"uploaded-card-file-name\">\n            {{ fileName }}\n        </p>\n    </section>\n    <tag [props]=\"statusProps\" class=\"uploaded-card-tag\" />\n</section>", styles: [".upload-card-container{display:flex;align-items:center;border:2px solid #E6E6E6;width:325px;height:60px;padding:0 10px;box-shadow:0 0 6px #00000029;border-radius:5px;margin-top:5px;margin-left:2px;margin-bottom:5px}.uploaded-card-data{display:flex;flex-direction:column;justify-content:center;margin-right:auto}.uploaded-card-date{font-size:10px;margin-top:15px}.uploaded-card-file-name{font-size:14px;margin-top:-5px}\n"], dependencies: [{ kind: "component", type: Tag, selector: "tag", inputs: ["props"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UploadedFileCard, decorators: [{
            type: Component,
            args: [{ selector: 'uploaded-file-card', template: "<section class=\"upload-card-container\">\n    <section class=\"uploaded-card-data\">\n        <p class=\"uploaded-card-date\">\n            {{ formatDateTime(dateTime) }}\n        </p>\n        <p class=\"uploaded-card-file-name\">\n            {{ fileName }}\n        </p>\n    </section>\n    <tag [props]=\"statusProps\" class=\"uploaded-card-tag\" />\n</section>", styles: [".upload-card-container{display:flex;align-items:center;border:2px solid #E6E6E6;width:325px;height:60px;padding:0 10px;box-shadow:0 0 6px #00000029;border-radius:5px;margin-top:5px;margin-left:2px;margin-bottom:5px}.uploaded-card-data{display:flex;flex-direction:column;justify-content:center;margin-right:auto}.uploaded-card-date{font-size:10px;margin-top:15px}.uploaded-card-file-name{font-size:14px;margin-top:-5px}\n"] }]
        }], propDecorators: { dateTime: [{
                type: Input
            }], fileName: [{
                type: Input
            }], statusProps: [{
                type: Input
            }] } });

class Upload {
    operationsService;
    leafProviderdata;
    i18n;
    translate;
    apiKey;
    leafUser;
    title;
    filesTimeRange;
    lang;
    getLeafBatchData = new EventEmitter();
    isDraggingOver = false;
    isStatusError = false;
    errorMessage = '';
    isApiError = false;
    historicalFiles = new Array();
    _toDelete;
    container = container$1;
    globalStyle = globalStyle;
    baseboardContainer = baseboardContainer$3;
    text = simpleText;
    input = input$2;
    uploadedFilesBox = uploadedFilesBox;
    dropZoneDiv = dropZoneDiv;
    uploadedFilesText = uploadedFilesText;
    scrollable = scrollableContainer;
    titleTextStyle;
    recentFilesTextStyle;
    lastDaysTextStyle;
    containerStyle;
    initStyle() {
        this.titleTextStyle = {
            fontSize: 23,
            fontWeight: 'bold',
            margin: '0px 0px 25px 20px',
        };
        this.recentFilesTextStyle = {
            fontSize: 18,
            margin: '20px 0px 0px 20px',
        };
        this.lastDaysTextStyle = {
            fontSize: 12,
            margin: '0px 0px 15px 21px',
        };
        this.containerStyle = {
            width: 400,
        };
    }
    constructor(operationsService, leafProviderdata, i18n, translate) {
        this.operationsService = operationsService;
        this.leafProviderdata = leafProviderdata;
        this.i18n = i18n;
        this.translate = translate;
        this.initStyle();
    }
    ngOnInit() {
        this.retrieveHistoricalFiles();
        this.i18n.init(this.translate, this.lang);
    }
    ngOnChanges() {
        this.i18n.changeLanguage(this.lang);
    }
    async retrieveHistoricalFiles() {
        try {
            const data = await this.operationsService.leafGetBatch(this.apiKey, this.leafUser, getISOStringDatetime(this.filesTimeRange));
            data.reverse();
            this.historicalFiles = data;
        }
        catch (e) {
            this.isApiError = true;
        }
    }
    get toDelete() {
        return this._toDelete;
    }
    set toDelete(value) {
        this._toDelete = value;
        this.removeFromList(value);
    }
    removeFromList(file) {
        const index = this.historicalFiles.findIndex((object) => {
            return object.fileName === file?.fileName;
        });
        this.historicalFiles.splice(index, 1);
    }
    handleDrag(event) {
        event.preventDefault();
        event.stopPropagation();
        if (event.type === 'dragenter' || event.type === 'dragover') {
            this.isDraggingOver = true;
        }
        else if (event.type === 'dragleave') {
            this.isDraggingOver = false;
        }
    }
    clearStatusError() {
        this.isStatusError = false;
    }
    async handleDrop(event) {
        event.preventDefault();
        event.stopPropagation();
        this.isDraggingOver = false;
        if (event.dataTransfer) {
            this.updateAndSend(Array.from(event.dataTransfer.files)).catch((e) => {
                this.errorMessage = this.handleErrorMessage();
                this.isStatusError = true;
            });
        }
    }
    async updateAndSend(files) {
        const isReadyToUpload = files.map((file) => {
            return {
                file: file,
                fileName: file.name,
                status: UploadStatus.UPLOADING,
                uploadTimestamp: new Date().toString(),
                newFile: true,
            };
        });
        const isFileAlreadyUploaded = this.historicalFiles.filter((object1) => isReadyToUpload.some((object2) => object1.fileName === object2.fileName)).length > 0;
        if (!isFileAlreadyUploaded) {
            this.historicalFiles = [...isReadyToUpload, ...this.historicalFiles];
            await this.uploadFiles(isReadyToUpload);
        }
        else {
            alert('One or many of your files were already Uploaded, please change the file(s) name(s)');
        }
    }
    async uploadFiles(files) {
        for (const file of files) {
            try {
                if (file.newFile) {
                    const response = await this.operationsService.leafFileUpload(this.leafUser, this.apiKey, file.file);
                    this.historicalFiles = this.historicalFiles.map((item) => {
                        if (item.newFile) {
                            return { ...item, status: response.status };
                        }
                        else {
                            return item;
                        }
                    });
                    this.leafProviderdata.addLeafBatchId(response.id);
                    this.getLeafBatchData.emit(this.leafProviderdata.leafBatchIds);
                }
            }
            catch (e) {
                if (axios.isAxiosError(e)) {
                    this.errorMessage = this.handleErrorMessage(e.response?.data.detail, e.response?.status, conformString(file.fileName));
                    this.isStatusError = true;
                    this.toDelete = file;
                }
            }
        }
    }
    handleErrorMessage(message, status, dynamicString) {
        if (status === 400 &&
            message ===
                'Sorry, we may not be able to process this file. Make sure the file is a .zip') {
            return `${dynamicString} is not supported. Only zip files are allowed.`;
        }
        return 'Sorry we were not able to process your request.';
    }
    conformString(string) {
        return conformString(string);
    }
    createTagProp(status) {
        switch (status) {
            case UploadStatus.PROCESSED:
                return TagValuesProps.PROCESSED;
            case UploadStatus.RECEIVED:
                return TagValuesProps.RECEIVED;
            case UploadStatus.FAILED:
                return TagValuesProps.ERROR;
            default:
                return TagValuesProps.RECEIVED;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Upload, deps: [{ token: OperationsService }, { token: LeafProviderData }, { token: I18n }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Upload, selector: "upload", inputs: { apiKey: "apiKey", leafUser: "leafUser", title: "title", filesTimeRange: "filesTimeRange", lang: "lang", toDelete: "toDelete" }, outputs: { getLeafBatchData: "getLeafBatchData" }, usesOnChanges: true, ngImport: i0, template: "<div\n  [ngClass]=\"[globalStyle, container(containerStyle)]\"\n  (dragenter)=\"handleDrag($event)\"\n  data-testid=\"drag-enter\"\n>\n  <ng-container *ngIf=\"isStatusError\">\n    <error-message\n      data-testid=\"error-message\"\n      [errorMessage]=\"errorMessage\"\n      [okClick]=\"clearStatusError()\"\n    />\n  </ng-container>\n  <ng-container *ngIf=\"isDraggingOver\">\n    <file-upload-overlay data-testid=\"overlay-component\" />\n  </ng-container>\n  <p [ngClass]=\"text(titleTextStyle)\" data-testid=\"title-text\">\n    {{ title }}\n  </p>\n  <drag-and-drop\n    leafUser=\"{leafUser}\"\n    apiKey=\"{apiKey}\"\n    [historicalFiles]=\"historicalFiles\"\n    (updateAndSendEvent)=\"updateAndSend($event)\"\n    data-testid=\"drag-and-drop-element\"\n  />\n  <p [ngClass]=\"text(recentFilesTextStyle)\" data-testid=\"recent-files-text\">\n    {{ \"screens.fileUpload.recentFiles\" | translate }}\n  </p>\n  <p [ngClass]=\"text(lastDaysTextStyle)\" data-testid=\"last-days-text\">\n    {{ \"screens.fileUpload.last\" | translate }} {{ filesTimeRange }}\n    {{ \"screens.fileUpload.days\" | translate }}\n  </p>\n  <div\n    [ngClass]=\"[\n      scrollable,\n      uploadedFilesBox({ historicalLenght: historicalFiles.length })\n    ]\"\n  >\n    <ng-container *ngIf=\"!historicalFiles.length\">\n      <p [ngClass]=\"uploadedFilesText\" data-testid=\"is-api-error-text\">\n        {{\n          isApiError\n            ? (\"screens.fileUpload.connectionError\" | translate)\n            : (\"screens.fileUpload.filesDisplay\" | translate)\n        }}\n      </p>\n    </ng-container>\n    <ng-container *ngIf=\"historicalFiles.length\">\n      <ng-container *ngFor=\"let file of historicalFiles\">\n        <uploaded-file-card\n          [dateTime]=\"file.uploadTimestamp\"\n          [fileName]=\"conformString(file.fileName)\"\n          [statusProps]=\"createTagProp(file.status)\"\n        ></uploaded-file-card>\n      </ng-container>\n    </ng-container>\n  </div>\n  <div [ngClass]=\"baseboardContainer\">\n    <baseboard />\n  </div>\n  <ng-container *ngIf=\"isDraggingOver\">\n    <div\n      [ngClass]=\"dropZoneDiv\"\n      (dragenter)=\"handleDrag($event)\"\n      (dragleave)=\"handleDrag($event)\"\n      (dragover)=\"handleDrag($event)\"\n      (drop)=\"handleDrop($event)\"\n      data-testid=\"drag-zone\"\n    ></div>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DragAndDrop, selector: "drag-and-drop", inputs: ["apiKey", "leafUser", "historicalFiles", "lang"], outputs: ["updateAndSendEvent"] }, { kind: "component", type: ErrorMessage, selector: "error-message", inputs: ["errorMessage", "okClick"] }, { kind: "component", type: FileUploadOverlay, selector: "file-upload-overlay" }, { kind: "component", type: Baseboard, selector: "baseboard" }, { kind: "component", type: UploadedFileCard, selector: "uploaded-file-card", inputs: ["dateTime", "fileName", "statusProps"] }, { kind: "pipe", type: i1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Upload, decorators: [{
            type: Component,
            args: [{ selector: 'upload', template: "<div\n  [ngClass]=\"[globalStyle, container(containerStyle)]\"\n  (dragenter)=\"handleDrag($event)\"\n  data-testid=\"drag-enter\"\n>\n  <ng-container *ngIf=\"isStatusError\">\n    <error-message\n      data-testid=\"error-message\"\n      [errorMessage]=\"errorMessage\"\n      [okClick]=\"clearStatusError()\"\n    />\n  </ng-container>\n  <ng-container *ngIf=\"isDraggingOver\">\n    <file-upload-overlay data-testid=\"overlay-component\" />\n  </ng-container>\n  <p [ngClass]=\"text(titleTextStyle)\" data-testid=\"title-text\">\n    {{ title }}\n  </p>\n  <drag-and-drop\n    leafUser=\"{leafUser}\"\n    apiKey=\"{apiKey}\"\n    [historicalFiles]=\"historicalFiles\"\n    (updateAndSendEvent)=\"updateAndSend($event)\"\n    data-testid=\"drag-and-drop-element\"\n  />\n  <p [ngClass]=\"text(recentFilesTextStyle)\" data-testid=\"recent-files-text\">\n    {{ \"screens.fileUpload.recentFiles\" | translate }}\n  </p>\n  <p [ngClass]=\"text(lastDaysTextStyle)\" data-testid=\"last-days-text\">\n    {{ \"screens.fileUpload.last\" | translate }} {{ filesTimeRange }}\n    {{ \"screens.fileUpload.days\" | translate }}\n  </p>\n  <div\n    [ngClass]=\"[\n      scrollable,\n      uploadedFilesBox({ historicalLenght: historicalFiles.length })\n    ]\"\n  >\n    <ng-container *ngIf=\"!historicalFiles.length\">\n      <p [ngClass]=\"uploadedFilesText\" data-testid=\"is-api-error-text\">\n        {{\n          isApiError\n            ? (\"screens.fileUpload.connectionError\" | translate)\n            : (\"screens.fileUpload.filesDisplay\" | translate)\n        }}\n      </p>\n    </ng-container>\n    <ng-container *ngIf=\"historicalFiles.length\">\n      <ng-container *ngFor=\"let file of historicalFiles\">\n        <uploaded-file-card\n          [dateTime]=\"file.uploadTimestamp\"\n          [fileName]=\"conformString(file.fileName)\"\n          [statusProps]=\"createTagProp(file.status)\"\n        ></uploaded-file-card>\n      </ng-container>\n    </ng-container>\n  </div>\n  <div [ngClass]=\"baseboardContainer\">\n    <baseboard />\n  </div>\n  <ng-container *ngIf=\"isDraggingOver\">\n    <div\n      [ngClass]=\"dropZoneDiv\"\n      (dragenter)=\"handleDrag($event)\"\n      (dragleave)=\"handleDrag($event)\"\n      (dragover)=\"handleDrag($event)\"\n      (drop)=\"handleDrop($event)\"\n      data-testid=\"drag-zone\"\n    ></div>\n  </ng-container>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: OperationsService }, { type: LeafProviderData }, { type: I18n }, { type: i1.TranslateService }]; }, propDecorators: { apiKey: [{
                type: Input
            }], leafUser: [{
                type: Input
            }], title: [{
                type: Input
            }], filesTimeRange: [{
                type: Input
            }], lang: [{
                type: Input
            }], getLeafBatchData: [{
                type: Output
            }], toDelete: [{
                type: Input
            }] } });

class FileUpload {
    screenProvider;
    apiKey;
    leafUser;
    title;
    filesTimeRange;
    companyName;
    companyLogo;
    lang;
    getLeafBatchData = new EventEmitter();
    currentScreen;
    screenChangeSubscription;
    constructor(screenProvider) {
        this.screenProvider = screenProvider;
    }
    ngOnInit() {
        this.currentScreen = localStorage.getItem('dismissWarningScreen') !== 'true' ? 'warning' : 'upload';
        this.screenChangeSubscription = this.screenProvider.screenChangeObservable.subscribe(() => {
            this.currentScreen = this.screenProvider.getCurrentScreen();
        });
    }
    ngOnDestroy() {
        this.screenChangeSubscription.unsubscribe();
    }
    handleGetLeafBatchData(value) {
        this.getLeafBatchData.emit(value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUpload, deps: [{ token: ScreenProvider }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: FileUpload, selector: "file-upload", inputs: { apiKey: "apiKey", leafUser: "leafUser", title: "title", filesTimeRange: "filesTimeRange", companyName: "companyName", companyLogo: "companyLogo", lang: "lang" }, outputs: { getLeafBatchData: "getLeafBatchData" }, ngImport: i0, template: "<ng-container [ngSwitch]=\"currentScreen\">\n  <warning\n    *ngSwitchCase=\"'warning'\"\n    [companyName]=\"companyName\"\n    [companyLogo]=\"companyLogo\"\n    [widget]=\"'upload'\"\n    [lang]=\"lang\"\n  ></warning>\n\n  <upload\n    *ngSwitchCase=\"'upload'\"\n    [title]=\"title\"\n    [filesTimeRange]=\"filesTimeRange\"\n    [apiKey]=\"apiKey\"\n    [leafUser]=\"leafUser\"\n    (getLeafBatchData)=\"handleGetLeafBatchData($event)\"\n  ></upload>\n</ng-container>", dependencies: [{ kind: "directive", type: i1$1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: Warning, selector: "warning", inputs: ["companyName", "companyLogo", "widget", "lang"] }, { kind: "component", type: Upload, selector: "upload", inputs: ["apiKey", "leafUser", "title", "filesTimeRange", "lang", "toDelete"], outputs: ["getLeafBatchData"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUpload, decorators: [{
            type: Component,
            args: [{ selector: 'file-upload', template: "<ng-container [ngSwitch]=\"currentScreen\">\n  <warning\n    *ngSwitchCase=\"'warning'\"\n    [companyName]=\"companyName\"\n    [companyLogo]=\"companyLogo\"\n    [widget]=\"'upload'\"\n    [lang]=\"lang\"\n  ></warning>\n\n  <upload\n    *ngSwitchCase=\"'upload'\"\n    [title]=\"title\"\n    [filesTimeRange]=\"filesTimeRange\"\n    [apiKey]=\"apiKey\"\n    [leafUser]=\"leafUser\"\n    (getLeafBatchData)=\"handleGetLeafBatchData($event)\"\n  ></upload>\n</ng-container>" }]
        }], ctorParameters: function () { return [{ type: ScreenProvider }]; }, propDecorators: { apiKey: [{
                type: Input
            }], leafUser: [{
                type: Input
            }], title: [{
                type: Input
            }], filesTimeRange: [{
                type: Input
            }], companyName: [{
                type: Input
            }], companyLogo: [{
                type: Input
            }], lang: [{
                type: Input
            }], getLeafBatchData: [{
                type: Output
            }] } });

class CustomButtonModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: CustomButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: CustomButtonModule, declarations: [CustomButton], imports: [CommonModule], exports: [CustomButton] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: CustomButtonModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: CustomButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CustomButton],
                    imports: [CommonModule],
                    exports: [CustomButton],
                }]
        }] });

class LeafLogoModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafLogoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: LeafLogoModule, declarations: [LeafLogo], imports: [CommonModule], exports: [LeafLogo] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafLogoModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafLogoModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        LeafLogo,
                    ],
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        LeafLogo
                    ]
                }]
        }] });

class WarningModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: WarningModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: WarningModule, declarations: [Warning, ArrowRightArrowLeftSolid, EyeSlashSolid], imports: [CommonModule, CustomButtonModule, LeafLogoModule, i1.TranslateModule], exports: [Warning] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: WarningModule, providers: [ScreenProvider, I18n], imports: [CommonModule, CustomButtonModule, LeafLogoModule,
            TranslateModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: WarningModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [Warning, ArrowRightArrowLeftSolid, EyeSlashSolid],
                    imports: [CommonModule, CustomButtonModule, LeafLogoModule,
                        TranslateModule.forRoot(),
                    ],
                    providers: [ScreenProvider, I18n],
                    exports: [Warning],
                }]
        }] });

class DragAndDropModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: DragAndDropModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: DragAndDropModule, declarations: [DragAndDrop,
            FilesDuotone], imports: [CommonModule, i1.TranslateModule], exports: [DragAndDrop] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: DragAndDropModule, providers: [I18n], imports: [CommonModule,
            TranslateModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: DragAndDropModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DragAndDrop,
                        FilesDuotone
                    ],
                    imports: [
                        CommonModule,
                        TranslateModule.forRoot(),
                    ],
                    providers: [I18n],
                    exports: [
                        DragAndDrop
                    ]
                }]
        }] });

class ErrorMessageModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ErrorMessageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: ErrorMessageModule, declarations: [ErrorMessage,
            WarningIcon], imports: [CommonModule], exports: [ErrorMessage] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ErrorMessageModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ErrorMessageModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ErrorMessage,
                        WarningIcon
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        ErrorMessage
                    ]
                }]
        }] });

class FileUploadOverlayModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUploadOverlayModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: FileUploadOverlayModule, declarations: [FileUploadOverlay,
            FileArrowUpRegular], imports: [CommonModule], exports: [FileUploadOverlay] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUploadOverlayModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUploadOverlayModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        FileUploadOverlay,
                        FileArrowUpRegular
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        FileUploadOverlay
                    ]
                }]
        }] });

class BaseboardModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BaseboardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: BaseboardModule, declarations: [Baseboard,
            LeafLogoWithText], imports: [CommonModule], exports: [Baseboard] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BaseboardModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BaseboardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Baseboard,
                        LeafLogoWithText
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        Baseboard
                    ]
                }]
        }] });

class TagModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: TagModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: TagModule, declarations: [Tag], imports: [CommonModule], exports: [Tag] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: TagModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: TagModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Tag,
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        Tag
                    ]
                }]
        }] });

class UploadedFileCardModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UploadedFileCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: UploadedFileCardModule, declarations: [UploadedFileCard], imports: [TagModule], exports: [UploadedFileCard] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UploadedFileCardModule, imports: [TagModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UploadedFileCardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [UploadedFileCard],
                    imports: [TagModule],
                    exports: [UploadedFileCard],
                }]
        }] });

class UploadModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UploadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: UploadModule, declarations: [Upload], imports: [CommonModule,
            DragAndDropModule,
            ErrorMessageModule,
            FileUploadOverlayModule,
            BaseboardModule,
            UploadedFileCardModule, i1.TranslateModule], exports: [Upload] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UploadModule, providers: [I18n], imports: [CommonModule,
            DragAndDropModule,
            ErrorMessageModule,
            FileUploadOverlayModule,
            BaseboardModule,
            UploadedFileCardModule,
            TranslateModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UploadModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Upload,
                    ],
                    imports: [
                        CommonModule,
                        DragAndDropModule,
                        ErrorMessageModule,
                        FileUploadOverlayModule,
                        BaseboardModule,
                        UploadedFileCardModule,
                        TranslateModule.forRoot(),
                    ],
                    providers: [I18n],
                    exports: [
                        Upload
                    ]
                }]
        }] });

class FileUploadModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUploadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: FileUploadModule, declarations: [FileUpload], imports: [CommonModule,
            WarningModule,
            UploadModule], exports: [FileUpload] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUploadModule, providers: [ScreenProvider, I18n], imports: [CommonModule,
            WarningModule,
            UploadModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: FileUploadModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        FileUpload,
                    ],
                    imports: [
                        CommonModule,
                        WarningModule,
                        UploadModule
                    ],
                    providers: [ScreenProvider, I18n],
                    exports: [
                        FileUpload
                    ]
                }]
        }] });

const centralizedContainer$1 = css `
    display: flex;
    flex-direction: column;
    align-items: center;
`;
const providerListContainer = css `
    display: flex;
    flex-direction: column;
    align-items: center;
`;
const providerCardContainer = css `
    max-height: 210px;
`;
const baseboardContainer$2 = css `
    display: flex;
    justify-content: flex-end;
`;
const notConnectedProviders = css `
    opacity: 0.5;
`;
const providerCard$1 = css `
    cursor: pointer;
`;

class UsermanagementService {
    async leafGetAppKeys(apiKey) {
        intercept(apiKey);
        const { data } = await api.get('services/usermanagement/api/app-keys');
        return data;
    }
    ;
    async leafProviderConnByLeafUser(apiKey, leafUser) {
        intercept(apiKey);
        const { data } = await api.get(`services/usermanagement/api/users/${leafUser}/provider-connections`);
        return data;
    }
    ;
    async getOrganizations(provider, apiKey, leafUser) {
        intercept(apiKey);
        const { data } = await api.get(`services/usermanagement/api/users/${leafUser}/organizations/${provider}`);
        return data;
    }
    ;
    async getProvidersCredentials(provider, apiKey, leafUser) {
        intercept(apiKey);
        const { data } = await api.get(`services/usermanagement/api/users/${leafUser}/${getLeafProviderInfo(provider)?.providerNameLowerCase}-credentials`);
        return data;
    }
    ;
    async getProviderAuthenticationUrl(provider, appName, apiKey, leafUser, clientEnvironment) {
        let response;
        intercept(apiKey);
        switch (provider) {
            case 'JohnDeere':
            case 'CNHI':
                response = await api.post(`services/usermanagement/api/users/${leafUser}/provider-connections/${provider}/${appName}/${clientEnvironment}`, '');
                break;
            default:
                response = await api.post(`services/usermanagement/api/users/${leafUser}/provider-connections/${provider}/${appName}`, '');
        }
        return response.data;
    }
    ;
    async getAuthenticationStatus(provider, apiKey, leafUser) {
        intercept(apiKey);
        const { data } = await api.get(`services/usermanagement/api/users/${leafUser}/provider-connections/${provider}/status`);
        return data;
    }
    ;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UsermanagementService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UsermanagementService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: UsermanagementService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class LeafStore {
    selectedProvider;
    getSelectedProvider() {
        return this.selectedProvider.selectedProvider;
    }
    getProviderSettings() {
        return this.selectedProvider.providerAppSettings;
    }
    setSelectedProvider(providerSelected) {
        this.selectedProvider = providerSelected;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafStore, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const leafHeaderContainer = css `
  display: flex;
  align-items: center;
  justify-content: center;
`;
const leafHeaderImage = css `
  width: 2rem;
  margin-right: 1rem;
`;
const backButton = css `
  width: 1.5rem;
  height: 1.5rem;
  position: absolute;
  left: 0;
  margin-left: 2rem;
  cursor: pointer;
`;

class BackButton {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BackButton, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: BackButton, selector: "back-button", ngImport: i0, template: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 448 512\">\n    <path\n        fill=\"#000000\"\n        d=\"M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H109.3l105.3-105.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z\"\n    />\n</svg>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BackButton, decorators: [{
            type: Component,
            args: [{ selector: 'back-button', template: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 448 512\">\n    <path\n        fill=\"#000000\"\n        d=\"M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H109.3l105.3-105.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z\"\n    />\n</svg>\n" }]
        }] });

class LeafHeader {
    screenProvider;
    globalStyle = globalStyle;
    simpleText = simpleText;
    leafHeaderContainer = leafHeaderContainer;
    leafHeaderImage = leafHeaderImage;
    backButton = backButton;
    leafTextStyle;
    currentScreen;
    initStyle() {
        this.leafTextStyle = {
            fontSize: 20,
        };
    }
    constructor(screenProvider) {
        this.screenProvider = screenProvider;
        this.initStyle();
    }
    ngOnInit() {
        this.currentScreen = this.screenProvider.getCurrentScreen();
    }
    handleBackButton() {
        this.screenProvider.screenTransition('list');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafHeader, deps: [{ token: ScreenProvider }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: LeafHeader, selector: "leaf-header", ngImport: i0, template: "<div [ngClass]=\"[globalStyle, leafHeaderContainer]\">\n  <back-button\n    *ngIf=\"currentScreen === 'provider'\"\n    [ngClass]=\"backButton\"\n    (click)=\"handleBackButton()\"\n    data-testid=\"back-button\" \n  />\n  <leaf-logo [ngClass]=\"leafHeaderImage\" data-testid=\"leaf-header-img\" />\n  <p [ngClass]=\"simpleText(leafTextStyle)\">Leaf</p>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: LeafLogo, selector: "leaf-logo" }, { kind: "component", type: BackButton, selector: "back-button" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafHeader, decorators: [{
            type: Component,
            args: [{ selector: 'leaf-header', template: "<div [ngClass]=\"[globalStyle, leafHeaderContainer]\">\n  <back-button\n    *ngIf=\"currentScreen === 'provider'\"\n    [ngClass]=\"backButton\"\n    (click)=\"handleBackButton()\"\n    data-testid=\"back-button\" \n  />\n  <leaf-logo [ngClass]=\"leafHeaderImage\" data-testid=\"leaf-header-img\" />\n  <p [ngClass]=\"simpleText(leafTextStyle)\">Leaf</p>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: ScreenProvider }]; } });

const providerCard = css `
    display: flex;
    align-items: center;
    width: 340px;
    height: 60px;
    box-shadow: 0px 3px 6px #00000029;
    border-radius: 5px;
    padding-left: 10px;
    margin-bottom: 15px;
`;
const providerCardImage = css `
    width: 40px;
    height: 40px;
    border-radius: 5px;
    margin-top: 4px;
`;
const providerCardData = css `
    margin-left: 10px;
`;
const tagContainer = css `
    margin: -30px 10px 0px auto;
`;

class ProviderCard {
    globalStyle = globalStyle;
    providerCard = providerCard;
    providerCardImage = providerCardImage;
    providerCardData = providerCardData;
    tagContainer = tagContainer;
    simpleText = simpleText;
    providerNameStyle;
    providerUrlStyle;
    constructor() {
        this.providerNameStyle = {
            fontSize: 16,
            fontWeight: 'bold',
            margin: '10px 0px -10px 0px'
        };
        this.providerUrlStyle = {
            fontSize: 10
        };
    }
    providerImage;
    providerName;
    providerUrl;
    tagProps;
    onClick = new EventEmitter();
    handleClick() {
        this.onClick.emit();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProviderCard, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: ProviderCard, selector: "provider-card", inputs: { providerImage: "providerImage", providerName: "providerName", providerUrl: "providerUrl", tagProps: "tagProps" }, outputs: { onClick: "onClick" }, ngImport: i0, template: "<div [ngClass]=\"[globalStyle, providerCard]\" (click)=\"handleClick()\" data-testid=\"provider-card\">\n    <div>\n        <img [ngClass]=\"providerCardImage\" [src]=\"providerImage\" alt=\"provider-image\">\n    </div>\n    <div [ngClass]=\"providerCardData\">\n        <p [ngClass]=\"simpleText(providerNameStyle)\" data-testid=\"provider-name\">{{providerName}}</p>\n        <p [ngClass]=\"simpleText(providerUrlStyle)\" data-testid=\"provider-url\">{{providerUrl}}</p>\n    </div>\n    <div *ngIf=\"tagProps\" [ngClass]=\"tagContainer\">\n        <tag [props]=\"tagProps\"></tag>\n    </div>\n</div>", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: Tag, selector: "tag", inputs: ["props"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProviderCard, decorators: [{
            type: Component,
            args: [{ selector: 'provider-card', template: "<div [ngClass]=\"[globalStyle, providerCard]\" (click)=\"handleClick()\" data-testid=\"provider-card\">\n    <div>\n        <img [ngClass]=\"providerCardImage\" [src]=\"providerImage\" alt=\"provider-image\">\n    </div>\n    <div [ngClass]=\"providerCardData\">\n        <p [ngClass]=\"simpleText(providerNameStyle)\" data-testid=\"provider-name\">{{providerName}}</p>\n        <p [ngClass]=\"simpleText(providerUrlStyle)\" data-testid=\"provider-url\">{{providerUrl}}</p>\n    </div>\n    <div *ngIf=\"tagProps\" [ngClass]=\"tagContainer\">\n        <tag [props]=\"tagProps\"></tag>\n    </div>\n</div>" }]
        }], ctorParameters: function () { return []; }, propDecorators: { providerImage: [{
                type: Input
            }], providerName: [{
                type: Input
            }], providerUrl: [{
                type: Input
            }], tagProps: [{
                type: Input
            }], onClick: [{
                type: Output
            }] } });
;

const input = css `
    padding-right: 25px;
    background: url('https://static.thenounproject.com/png/101791-200.png') no-repeat right;
    background-size: 15px;
    background-position-x: 315px;
    background-color: #FFFFFF;
    color: #171717;
    width: 305px;
    height: 35px;
    font-size: 11px;
    font-family: 'Manrope';
    border: none;
    border-radius: 5px;
    padding-left: 10px;
    box-shadow: 0px 3px 6px #00000029;
`;

class SearchBar {
    i18n;
    translate;
    input = input;
    inputSubject = new Subject();
    lang;
    textChanged = new EventEmitter();
    constructor(i18n, translate) {
        this.i18n = i18n;
        this.translate = translate;
    }
    ngOnInit() {
        this.inputSubject.pipe(debounceTime(100))
            .subscribe((text) => this.textChanged.emit(text));
        this.i18n.init(this.translate, this.lang);
    }
    ngOnChanges() {
        this.i18n.changeLanguage(this.lang);
    }
    handleTextChange(event) {
        const value = event.target.value;
        this.inputSubject.next(value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: SearchBar, deps: [{ token: I18n }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: SearchBar, selector: "search-bar", inputs: { lang: "lang" }, outputs: { textChanged: "textChanged" }, usesOnChanges: true, ngImport: i0, template: "<input [ngClass]=\"input\" type=\"text\" placeholder=\"{{ 'screens.providerList.search' | translate }}\" (input)=\"handleTextChange($event)\">", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: i1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: SearchBar, decorators: [{
            type: Component,
            args: [{ selector: 'search-bar', template: "<input [ngClass]=\"input\" type=\"text\" placeholder=\"{{ 'screens.providerList.search' | translate }}\" (input)=\"handleTextChange($event)\">" }]
        }], ctorParameters: function () { return [{ type: I18n }, { type: i1.TranslateService }]; }, propDecorators: { lang: [{
                type: Input
            }], textChanged: [{
                type: Output
            }] } });
;

class List {
    usermanagementService;
    store;
    leafProviderData;
    screenProvider;
    i18n;
    translate;
    globalStyle = globalStyle;
    simpleText = simpleText;
    scrollable = scrollableContainer;
    container = container$1;
    centralizedContainer = centralizedContainer$1;
    providerListContainer = providerListContainer;
    baseboardContainer = baseboardContainer$2;
    providerCardContainer = providerCardContainer;
    notConnectedProviders = notConnectedProviders;
    providerCard = providerCard$1;
    containerStyle;
    titleTextStyle;
    providerStatusStyle;
    providersLoadTextStyle;
    apiKey;
    leafUser;
    getWidgetStatus = new EventEmitter();
    lang;
    PROVIDER_SCREEN = 'provider';
    ORGANIZATION_SCREEN = 'organization';
    isLoadingData;
    incompleteProviders = [];
    unauthenticatedProviders = [];
    providersAvailable = [];
    filteredList = [];
    initStyle() {
        this.titleTextStyle = {
            fontSize: 21,
            fontWeight: 'bold',
            margin: '30px 115px 20px 0px',
        };
        this.containerStyle = {
            width: 400,
        };
        this.providerStatusStyle = {
            fontSize: 14,
            fontWeight: 'bold',
            margin: '20px 255px 10px 0px',
        };
        this.providersLoadTextStyle = {
            fontSize: 14,
            fontWeight: 'bold',
            margin: '20px 195px 10px 0px',
        };
    }
    constructor(usermanagementService, store, leafProviderData, screenProvider, i18n, translate) {
        this.usermanagementService = usermanagementService;
        this.store = store;
        this.leafProviderData = leafProviderData;
        this.screenProvider = screenProvider;
        this.i18n = i18n;
        this.translate = translate;
        this.initStyle();
    }
    ngOnInit() {
        this.checkProviders().finally(() => {
            this.getWidgetStatus.emit({
                providersConnected: this.leafProviderData.providersConnected,
                providerWidgetStatus: this.leafProviderData.providerWidgetStatus,
            });
        });
        this.i18n.init(this.translate, this.lang);
    }
    ngOnChanges() {
        this.i18n.changeLanguage(this.lang);
    }
    async checkProviders() {
        this.isLoadingData = true;
        const connectedProviders = await this.usermanagementService.leafGetAppKeys(this.apiKey);
        const collectedProvidersAvailable = this.collectProvidersAvailable(connectedProviders);
        const verifiedUsersConnection = await this.usermanagementService.leafProviderConnByLeafUser(this.apiKey, this.leafUser);
        await this.verifyIncompleteProviders(verifiedUsersConnection);
        await this.checkProviderAuth(verifiedUsersConnection);
        this.sortProvidersAppSettings(collectedProvidersAvailable);
        const collectedProviders = collectedProvidersAvailable.map((availableProvider) => {
            if (verifiedUsersConnection.includes(availableProvider.provider)) {
                availableProvider.incomplete = this.incompleteProviders.includes(availableProvider.provider);
                availableProvider.unauthenticated = this.unauthenticatedProviders.includes(availableProvider.provider);
                if (availableProvider.unauthenticated) {
                    this.defineUnauthentication(availableProvider);
                }
                else if (availableProvider.incomplete) {
                    this.defineIncomplete(availableProvider);
                }
                else {
                    this.leafProviderData.providersConnected &&
                        this.newProviderConnected(availableProvider.provider).catch(() => this.leafProviderData.setProviderWidgetStatus({
                            code: -1,
                            message: 'Error',
                        }));
                    return {
                        ...availableProvider,
                        ...getLeafProviderInfo(availableProvider.provider),
                        onClick: this.defineProviderOnClick(availableProvider, this.PROVIDER_SCREEN),
                        connected: true,
                    };
                }
                return {
                    ...availableProvider,
                    ...getLeafProviderInfo(availableProvider.provider),
                };
            }
            return {
                ...availableProvider,
                ...getLeafProviderInfo(availableProvider.provider),
                onClick: this.defineProviderOnClick(availableProvider, this.PROVIDER_SCREEN),
                connected: false,
                incomplete: false,
                unauthenticated: false,
            };
        });
        if (collectedProviders.toString() != this.providersAvailable.toString()) {
            this.providersAvailable = this.sortByProviderOrder(collectedProviders);
        }
        this.filteredList = this.providersAvailable.filter((item) => item.provider !== 'Geofolia');
        this.isLoadingData = false;
    }
    async checkProviderAuth(verifiedUsersConnection) {
        const validProviders = ['JohnDeere', 'Trimble', 'CNHI', 'AgLeader', 'ClimateFieldView'];
        const filteredConnectedProviders = verifiedUsersConnection.filter((provider) => validProviders.includes(provider));
        for (let connectedProvider of filteredConnectedProviders) {
            const { status } = await this.usermanagementService.getAuthenticationStatus(connectedProvider, this.apiKey, this.leafUser);
            if (status === 'UNAUTHENTICATED')
                this.unauthenticatedProviders.push(connectedProvider);
        }
    }
    ;
    async verifyIncompleteProviders(verifiedUsersConnection) {
        const filteredProviders = verifiedUsersConnection.filter((provider) => provider === 'JohnDeere' || provider === 'Trimble');
        for (let provider of filteredProviders) {
            const isIncomplete = await this.checkOrganizations(provider);
            if (isIncomplete) {
                this.incompleteProviders.push(provider);
            }
        }
    }
    async checkOrganizations(provider) {
        const { connectedOrganizations } = await this.usermanagementService.getOrganizations(provider, this.apiKey, this.leafUser);
        return connectedOrganizations.length <= 0;
    }
    sortProvidersAppSettings(collectedProvidersAvailable) {
        collectedProvidersAvailable.forEach((provider) => {
            provider.appSettings?.sort((firstAppName, secondAppName) => {
                if (firstAppName.clientEnvironment === 'PRODUCTION' &&
                    secondAppName.clientEnvironment === 'STAGE') {
                    return -1;
                }
                if (firstAppName.clientEnvironment === 'STAGE' &&
                    secondAppName.clientEnvironment === 'PRODUCTION') {
                    return 1;
                }
                return 0;
            });
        });
    }
    collectProvidersAvailable(connectedProviders) {
        return Object.values(connectedProviders.reduce((acc, { provider, appName, clientEnvironment }) => {
            if (!acc[provider]) {
                acc[provider] = { provider, appSettings: [], providerName: '' };
            }
            acc[provider].appSettings?.push({ appName, clientEnvironment });
            return acc;
        }, {}));
    }
    sortByProviderOrder(providersList) {
        if (providersList.length) {
            providersList.sort((firstProvider, secondProvider) => (firstProvider.providerOrder ?? 0) -
                (secondProvider.providerOrder ?? 0));
        }
        return providersList;
    }
    defineIncomplete(availableProvider) {
        availableProvider.onClick = this.defineProviderOnClick(availableProvider, this.ORGANIZATION_SCREEN);
        availableProvider.connected = false;
        availableProvider.incompleteTag = TagValuesProps.INCOMPLETE;
    }
    defineUnauthentication(availableProvider) {
        availableProvider.onClick = this.defineProviderOnClick(availableProvider, this.PROVIDER_SCREEN);
        availableProvider.connected = false;
        availableProvider.unauthenticatedTag = TagValuesProps.UNAUTHENTICATED;
    }
    defineProviderOnClick(availableProvider, nextScreen) {
        return () => {
            this.store.setSelectedProvider({
                selectedProvider: availableProvider.provider,
                providerAppSettings: availableProvider.appSettings?.shift(),
            });
            this.screenProvider.screenTransition(nextScreen);
        };
    }
    async newProviderConnected(provider) {
        if (doesNotContainsProviderNameInList(this.leafProviderData.providersConnected, provider)) {
            const connectedProviders = await this.usermanagementService.leafProviderConnByLeafUser(this.apiKey, this.leafUser);
            const newProvider = connectedProviders.find((connectedProvider) => connectedProvider === provider);
            if (newProvider) {
                this.leafProviderData.setProvidersConnected({
                    provider: newProvider,
                    createdTime: '',
                });
            }
        }
    }
    searchProvider(value) {
        if (value.trim() === '') {
            this.filteredList = this.providersAvailable;
        }
        else {
            const connected = this.providersAvailable.filter((provider) => provider.connected);
            const available = this.providersAvailable.filter((provider) => !provider.connected);
            const filtered = available.filter((provider) => provider.providerName?.toLowerCase().includes(value.toLowerCase()));
            this.filteredList = [...filtered, ...connected];
        }
    }
    renderProvidersSection(isConnected) {
        return isConnected
            ? this.providersAvailable.some((provider) => provider.connected === true)
            : this.providersAvailable.some((provider) => provider.connected === false);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: List, deps: [{ token: UsermanagementService }, { token: LeafStore }, { token: LeafProviderData }, { token: ScreenProvider }, { token: I18n }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: List, selector: "list", inputs: { apiKey: "apiKey", leafUser: "leafUser", lang: "lang" }, outputs: { getWidgetStatus: "getWidgetStatus" }, usesOnChanges: true, ngImport: i0, template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <leaf-header></leaf-header>\n  <div [ngClass]=\"centralizedContainer\">\n    <p data-testid=\"title-text\" [ngClass]=\"simpleText(titleTextStyle)\">\n      {{ \"screens.providerList.selectIntegration\" | translate }}\n    </p>\n    <search-bar (textChanged)=\"searchProvider($event)\"></search-bar>\n    <div *ngIf=\"isLoadingData\">\n      <p [ngClass]=\"simpleText(providersLoadTextStyle)\">Loading Providers...</p>\n    </div>\n    <div *ngIf=\"!isLoadingData && !filteredList.length\">\n      <p [ngClass]=\"simpleText(providersLoadTextStyle)\">\n        No providers available.\n      </p>\n    </div>\n  </div>\n  <div\n    *ngIf=\"!isLoadingData && filteredList.length\"\n    [ngClass]=\"providerListContainer\"\n  >\n    <div [ngClass]=\"centralizedContainer\">\n      <p\n        *ngIf=\"renderProvidersSection(false)\"\n        [ngClass]=\"simpleText(providerStatusStyle)\"\n      >\n        {{ \"screens.providerList.available\" | translate }}\n      </p>\n    </div>\n    <div [ngClass]=\"[scrollable, providerCardContainer]\">\n      <div *ngFor=\"let provider of filteredList\">\n        <div *ngIf=\"!provider.connected\">\n          <provider-card\n            [ngClass]=\"providerCard\"\n            [providerImage]=\"provider.providerLogo\"\n            [providerName]=\"provider.providerName\"\n            [providerUrl]=\"provider.providerUrl\"\n            [tagProps]=\"provider.unauthenticatedTag ?? provider.incompleteTag\"\n            (onClick)=\"provider.onClick?.()\"\n          >\n          </provider-card>\n        </div>\n      </div>\n    </div>\n\n    <div [ngClass]=\"centralizedContainer\">\n      <p\n        *ngIf=\"renderProvidersSection(true)\"\n        [ngClass]=\"simpleText(providerStatusStyle)\"\n      >\n        {{ \"screens.providerList.connected\" | translate }}\n      </p>\n    </div>\n    <div [ngClass]=\"[scrollable, providerCardContainer]\">\n      <div\n        *ngFor=\"let provider of filteredList\"\n        [ngClass]=\"notConnectedProviders\"\n      >\n        <div *ngIf=\"provider.connected\">\n          <provider-card\n            [providerImage]=\"provider.providerLogo\"\n            [providerName]=\"provider.providerName\"\n            [providerUrl]=\"provider.providerUrl\"\n          >\n          </provider-card>\n        </div>\n      </div>\n    </div>\n  </div>\n  <div [ngClass]=\"baseboardContainer\">\n    <baseboard />\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: LeafHeader, selector: "leaf-header" }, { kind: "component", type: ProviderCard, selector: "provider-card", inputs: ["providerImage", "providerName", "providerUrl", "tagProps"], outputs: ["onClick"] }, { kind: "component", type: SearchBar, selector: "search-bar", inputs: ["lang"], outputs: ["textChanged"] }, { kind: "component", type: Baseboard, selector: "baseboard" }, { kind: "pipe", type: i1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: List, decorators: [{
            type: Component,
            args: [{ selector: 'list', template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <leaf-header></leaf-header>\n  <div [ngClass]=\"centralizedContainer\">\n    <p data-testid=\"title-text\" [ngClass]=\"simpleText(titleTextStyle)\">\n      {{ \"screens.providerList.selectIntegration\" | translate }}\n    </p>\n    <search-bar (textChanged)=\"searchProvider($event)\"></search-bar>\n    <div *ngIf=\"isLoadingData\">\n      <p [ngClass]=\"simpleText(providersLoadTextStyle)\">Loading Providers...</p>\n    </div>\n    <div *ngIf=\"!isLoadingData && !filteredList.length\">\n      <p [ngClass]=\"simpleText(providersLoadTextStyle)\">\n        No providers available.\n      </p>\n    </div>\n  </div>\n  <div\n    *ngIf=\"!isLoadingData && filteredList.length\"\n    [ngClass]=\"providerListContainer\"\n  >\n    <div [ngClass]=\"centralizedContainer\">\n      <p\n        *ngIf=\"renderProvidersSection(false)\"\n        [ngClass]=\"simpleText(providerStatusStyle)\"\n      >\n        {{ \"screens.providerList.available\" | translate }}\n      </p>\n    </div>\n    <div [ngClass]=\"[scrollable, providerCardContainer]\">\n      <div *ngFor=\"let provider of filteredList\">\n        <div *ngIf=\"!provider.connected\">\n          <provider-card\n            [ngClass]=\"providerCard\"\n            [providerImage]=\"provider.providerLogo\"\n            [providerName]=\"provider.providerName\"\n            [providerUrl]=\"provider.providerUrl\"\n            [tagProps]=\"provider.unauthenticatedTag ?? provider.incompleteTag\"\n            (onClick)=\"provider.onClick?.()\"\n          >\n          </provider-card>\n        </div>\n      </div>\n    </div>\n\n    <div [ngClass]=\"centralizedContainer\">\n      <p\n        *ngIf=\"renderProvidersSection(true)\"\n        [ngClass]=\"simpleText(providerStatusStyle)\"\n      >\n        {{ \"screens.providerList.connected\" | translate }}\n      </p>\n    </div>\n    <div [ngClass]=\"[scrollable, providerCardContainer]\">\n      <div\n        *ngFor=\"let provider of filteredList\"\n        [ngClass]=\"notConnectedProviders\"\n      >\n        <div *ngIf=\"provider.connected\">\n          <provider-card\n            [providerImage]=\"provider.providerLogo\"\n            [providerName]=\"provider.providerName\"\n            [providerUrl]=\"provider.providerUrl\"\n          >\n          </provider-card>\n        </div>\n      </div>\n    </div>\n  </div>\n  <div [ngClass]=\"baseboardContainer\">\n    <baseboard />\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: UsermanagementService }, { type: LeafStore }, { type: LeafProviderData }, { type: ScreenProvider }, { type: I18n }, { type: i1.TranslateService }]; }, propDecorators: { apiKey: [{
                type: Input
            }], leafUser: [{
                type: Input
            }], getWidgetStatus: [{
                type: Output
            }], lang: [{
                type: Input
            }] } });

const centralizedContainer = css `
    display: flex;
    flex-direction: column;
    align-items: center;
`;
const providerData$1 = css `
    display: flex;
    margin-right: 90px;
`;
const providerLogo$1 = css `
    width: 50px;
    height: 50px;
    border-radius: 5px;
    margin: 8px 16px 0px 0px;
`;
const providerDescription = css `
    display: flex;
    margin: 20px 36px 0px 50px;

    @media screen and (max-width: 640px) {
        margin-left: 26vw;
        margin-right: 20vw;
    }
`;
const buttonContainer = css `
    display: flex;
    justify-content: center;
    margin: 50px 0px 0px 0px;
    padding-bottom: 50px;
`;

class Provider {
    store;
    usermanagementService;
    screenProvider;
    i18n;
    translate;
    globalStyle = globalStyle;
    simpleText = simpleText;
    container = container$1;
    providerData = providerData$1;
    providerLogo = providerLogo$1;
    providerDescription = providerDescription;
    buttonContainer = buttonContainer;
    centralizedContainer = centralizedContainer;
    providerNameStyle;
    providerTitleStyle;
    providerDescriptionStyle;
    providerDescriptionNumberStyle;
    continueButtonsStyle;
    containerStyle;
    authWindow;
    TRIMBLE = 'Trimble';
    JOHN_DEERE = 'JohnDeere';
    companyName;
    apiKey;
    leafUser;
    lang;
    currentProvider;
    providerName;
    providerLogoUrl;
    appName;
    clientEnvironment;
    initStyle() {
        this.providerNameStyle = {
            fontSize: 20,
            fontWeight: 'bold',
        };
        this.providerTitleStyle = {
            fontSize: 18,
            fontWeight: 'bold',
            margin: '30px 20px 0px 0px',
        };
        this.providerDescriptionNumberStyle = {
            fontSize: 18,
            fontWeight: 'bold',
        };
        this.providerDescriptionStyle = {
            fontSize: 18,
            margin: '18px 0px 0px 16px',
        };
        this.continueButtonsStyle = {
            padding: '0px 30px 0px 30px',
            backgroundColor: '#0f876d',
            color: 'white',
            fontSize: 14,
            height: 40,
        };
        this.containerStyle = {
            width: 400,
        };
    }
    constructor(store, usermanagementService, screenProvider, i18n, translate) {
        this.store = store;
        this.usermanagementService = usermanagementService;
        this.screenProvider = screenProvider;
        this.i18n = i18n;
        this.translate = translate;
        this.initStyle();
        const providerInfo = getLeafProviderInfo(this.store.getSelectedProvider());
        const providerSettings = this.store.getProviderSettings();
        this.currentProvider = providerInfo.provider;
        this.providerName = providerInfo.providerName;
        this.providerLogoUrl = providerInfo.providerLogo;
        this.appName = providerSettings?.appName ?? '';
        this.clientEnvironment = providerSettings?.clientEnvironment ?? '';
    }
    ngOnInit() {
        this.i18n.init(this.translate, this.lang);
    }
    ngOnChanges() {
        this.i18n.changeLanguage(this.lang);
    }
    shouldDisplayFourthDescription() {
        return (this.currentProvider === this.TRIMBLE ||
            this.currentProvider === this.JOHN_DEERE);
    }
    async executeIntegration() {
        this.screenProvider.screenTransition('load');
        const newWindow = window.open('about:blank');
        if (newWindow) {
            this.authWindow = newWindow;
        }
        try {
            const { signInUrl } = await this.usermanagementService.getProviderAuthenticationUrl(this.currentProvider, this.appName, this.apiKey, this.leafUser, this.clientEnvironment);
            this.authWindow.location.href = signInUrl;
        }
        catch (error) {
            console.error('Error fetching sign-in URL:', error);
            this.authWindow?.close();
            return;
        }
        setTimeout(() => {
            this.authWindow?.close();
        }, Number(environment.TIMEOUT_IN_MILLIS));
        const interval = setInterval(() => {
            this.verifyProviderSubscription(this.authWindow, interval);
        }, Number(environment.INTERVAL_IN_MILLIS));
    }
    async verifyProviderSubscription(authWindow, interval) {
        const userConnection = await this.usermanagementService.leafProviderConnByLeafUser(this.apiKey, this.leafUser);
        if (authWindow?.closed) {
            if (!userConnection.includes(this.currentProvider)) {
                this.screenProvider.screenTransition('list');
            }
            else {
                this.nextDefineScreen();
            }
            clearInterval(interval);
        }
        else {
            if (userConnection.includes(this.currentProvider)) {
                clearInterval(interval);
                authWindow?.close();
                this.nextDefineScreen();
            }
        }
    }
    nextDefineScreen() {
        switch (this.currentProvider) {
            case 'JohnDeere':
            case 'Trimble':
                this.screenProvider.screenTransition('organizations');
                break;
            default:
                this.screenProvider.screenTransition('done');
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Provider, deps: [{ token: LeafStore }, { token: UsermanagementService }, { token: ScreenProvider }, { token: I18n }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Provider, selector: "provider", inputs: { companyName: "companyName", apiKey: "apiKey", leafUser: "leafUser", lang: "lang" }, usesOnChanges: true, ngImport: i0, template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <leaf-header></leaf-header>\n  <div [ngClass]=\"centralizedContainer\">\n    <div [ngClass]=\"providerData\">\n      <img\n        data-testid=\"provider-logo\"\n        [ngClass]=\"providerLogo\"\n        [src]=\"providerLogoUrl\"\n        alt=\"provider-icon\"\n      />\n      <p data-testid=\"provider-name\" [ngClass]=\"simpleText(providerNameStyle)\">\n        {{ providerName }}\n      </p>\n    </div>\n    <p data-testid=\"provider-title\" [ngClass]=\"simpleText(providerTitleStyle)\">\n      {{ \"screens.provider.authenticateWith\" | translate }} {{ providerName }}\n    </p>\n  </div>\n  <div>\n    <div [ngClass]=\"providerDescription\">\n      <p\n        data-testid=\"number-one\"\n        [ngClass]=\"simpleText(providerDescriptionNumberStyle)\"\n      >\n        1.\n      </p>\n      <p\n        data-testid=\"provider-desc-one\"\n        [ngClass]=\"simpleText(providerDescriptionStyle)\"\n      >\n        {{ \"screens.provider.afterSelect\" | translate }} {{ currentProvider }}\n      </p>\n    </div>\n    <div [ngClass]=\"providerDescription\">\n      <p\n        data-testid=\"number-two\"\n        [ngClass]=\"simpleText(providerDescriptionNumberStyle)\"\n      >\n        2.\n      </p>\n      <p\n        data-testid=\"provider-desc-two\"\n        [ngClass]=\"simpleText(providerDescriptionStyle)\"\n      >\n        {{ \"screens.provider.authenticateWith\" | translate }}\n        {{ currentProvider }}\n      </p>\n    </div>\n    <div [ngClass]=\"providerDescription\">\n      <p\n        data-testid=\"number-three\"\n        [ngClass]=\"simpleText(providerDescriptionNumberStyle)\"\n      >\n        3.\n      </p>\n      <p\n        data-testid=\"provider-desc-three\"\n        [ngClass]=\"simpleText(providerDescriptionStyle)\"\n      >\n        {{ \"screens.provider.redirect\" | translate }} {{ companyName }}\n      </p>\n    </div>\n    <div\n      *ngIf=\"shouldDisplayFourthDescription()\"\n      [ngClass]=\"providerDescription\"\n    >\n      <p\n        data-testid=\"number-four\"\n        [ngClass]=\"simpleText(providerDescriptionNumberStyle)\"\n      >\n        4.\n      </p>\n      <p\n        data-testid=\"provider-desc-four\"\n        [ngClass]=\"simpleText(providerDescriptionStyle)\"\n      >\n        {{ \"screens.provider.allowAccess\" | translate }}\n      </p>\n    </div>\n  </div>\n  <div [ngClass]=\"buttonContainer\">\n    <custom-button\n      [buttonStyle]=\"continueButtonsStyle\"\n      text=\"{{ 'screens.provider.continue' | translate }}\"\n      (click)=\"executeIntegration()\"\n    ></custom-button>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: LeafHeader, selector: "leaf-header" }, { kind: "component", type: CustomButton, selector: "custom-button", inputs: ["text", "buttonStyle"] }, { kind: "pipe", type: i1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Provider, decorators: [{
            type: Component,
            args: [{ selector: 'provider', template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <leaf-header></leaf-header>\n  <div [ngClass]=\"centralizedContainer\">\n    <div [ngClass]=\"providerData\">\n      <img\n        data-testid=\"provider-logo\"\n        [ngClass]=\"providerLogo\"\n        [src]=\"providerLogoUrl\"\n        alt=\"provider-icon\"\n      />\n      <p data-testid=\"provider-name\" [ngClass]=\"simpleText(providerNameStyle)\">\n        {{ providerName }}\n      </p>\n    </div>\n    <p data-testid=\"provider-title\" [ngClass]=\"simpleText(providerTitleStyle)\">\n      {{ \"screens.provider.authenticateWith\" | translate }} {{ providerName }}\n    </p>\n  </div>\n  <div>\n    <div [ngClass]=\"providerDescription\">\n      <p\n        data-testid=\"number-one\"\n        [ngClass]=\"simpleText(providerDescriptionNumberStyle)\"\n      >\n        1.\n      </p>\n      <p\n        data-testid=\"provider-desc-one\"\n        [ngClass]=\"simpleText(providerDescriptionStyle)\"\n      >\n        {{ \"screens.provider.afterSelect\" | translate }} {{ currentProvider }}\n      </p>\n    </div>\n    <div [ngClass]=\"providerDescription\">\n      <p\n        data-testid=\"number-two\"\n        [ngClass]=\"simpleText(providerDescriptionNumberStyle)\"\n      >\n        2.\n      </p>\n      <p\n        data-testid=\"provider-desc-two\"\n        [ngClass]=\"simpleText(providerDescriptionStyle)\"\n      >\n        {{ \"screens.provider.authenticateWith\" | translate }}\n        {{ currentProvider }}\n      </p>\n    </div>\n    <div [ngClass]=\"providerDescription\">\n      <p\n        data-testid=\"number-three\"\n        [ngClass]=\"simpleText(providerDescriptionNumberStyle)\"\n      >\n        3.\n      </p>\n      <p\n        data-testid=\"provider-desc-three\"\n        [ngClass]=\"simpleText(providerDescriptionStyle)\"\n      >\n        {{ \"screens.provider.redirect\" | translate }} {{ companyName }}\n      </p>\n    </div>\n    <div\n      *ngIf=\"shouldDisplayFourthDescription()\"\n      [ngClass]=\"providerDescription\"\n    >\n      <p\n        data-testid=\"number-four\"\n        [ngClass]=\"simpleText(providerDescriptionNumberStyle)\"\n      >\n        4.\n      </p>\n      <p\n        data-testid=\"provider-desc-four\"\n        [ngClass]=\"simpleText(providerDescriptionStyle)\"\n      >\n        {{ \"screens.provider.allowAccess\" | translate }}\n      </p>\n    </div>\n  </div>\n  <div [ngClass]=\"buttonContainer\">\n    <custom-button\n      [buttonStyle]=\"continueButtonsStyle\"\n      text=\"{{ 'screens.provider.continue' | translate }}\"\n      (click)=\"executeIntegration()\"\n    ></custom-button>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: LeafStore }, { type: UsermanagementService }, { type: ScreenProvider }, { type: I18n }, { type: i1.TranslateService }]; }, propDecorators: { companyName: [{
                type: Input
            }], apiKey: [{
                type: Input
            }], leafUser: [{
                type: Input
            }], lang: [{
                type: Input
            }] } });

const rotate = keyframes `
  0% {
    transform: rotate(0deg);
  }

  25% {
    transform: rotate(90deg);
  }

  50% {
    transform: rotate(180deg);
  }

  75% {
    transform: rotate(270deg);
  }

  100% {
    transform: rotate(360deg);
  }
`;
const loadIcon = css `
    width: 40px;
    height: 50px;
    margin: 130px 0px -25px 0px;

    animation: ${rotate} 2s infinite linear;
`;
const loadData = css `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;
const baseboardContainer$1 = css `
    display: flex;
    justify-content: flex-end;
    margin: 100px 0px 0px 0px;
`;

class LoadIcon {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LoadIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: LoadIcon, selector: "load-icon", ngImport: i0, template: "<svg fill=\"#085B59\" viewBox=\"0 0 50 50\" xmlns=\"http://www.w3.org/2000/svg\" transform=\"matrix(1, 0, 0, 1, 0, 0)\"><g id=\"SVGRepo_bgCarrier\" stroke-width=\"0\"></g><g id=\"SVGRepo_tracerCarrier\" stroke-linecap=\"round\" stroke-linejoin=\"round\"></g><g id=\"SVGRepo_iconCarrier\"><path d=\"M41.9 23.9c-.3-6.1-4-11.8-9.5-14.4-6-2.7-13.3-1.6-18.3 2.6-4.8 4-7 10.5-5.6 16.6 1.3 6 6 10.9 11.9 12.5 7.1 2 13.6-1.4 17.6-7.2-3.6 4.8-9.1 8-15.2 6.9-6.1-1.1-11.1-5.7-12.5-11.7-1.5-6.4 1.5-13.1 7.2-16.4 5.9-3.4 14.2-2.1 18.1 3.7 1 1.4 1.7 3.1 2 4.8.3 1.4.2 2.9.4 4.3.2 1.3 1.3 3 2.8 2.1 1.3-.8 1.2-2.5 1.1-3.8 0-.4.1.7 0 0z\"></path></g></svg>" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LoadIcon, decorators: [{
            type: Component,
            args: [{ selector: 'load-icon', template: "<svg fill=\"#085B59\" viewBox=\"0 0 50 50\" xmlns=\"http://www.w3.org/2000/svg\" transform=\"matrix(1, 0, 0, 1, 0, 0)\"><g id=\"SVGRepo_bgCarrier\" stroke-width=\"0\"></g><g id=\"SVGRepo_tracerCarrier\" stroke-linecap=\"round\" stroke-linejoin=\"round\"></g><g id=\"SVGRepo_iconCarrier\"><path d=\"M41.9 23.9c-.3-6.1-4-11.8-9.5-14.4-6-2.7-13.3-1.6-18.3 2.6-4.8 4-7 10.5-5.6 16.6 1.3 6 6 10.9 11.9 12.5 7.1 2 13.6-1.4 17.6-7.2-3.6 4.8-9.1 8-15.2 6.9-6.1-1.1-11.1-5.7-12.5-11.7-1.5-6.4 1.5-13.1 7.2-16.4 5.9-3.4 14.2-2.1 18.1 3.7 1 1.4 1.7 3.1 2 4.8.3 1.4.2 2.9.4 4.3.2 1.3 1.3 3 2.8 2.1 1.3-.8 1.2-2.5 1.1-3.8 0-.4.1.7 0 0z\"></path></g></svg>" }]
        }] });

class Load {
    store;
    globalStyle = globalStyle;
    simpleText = simpleText;
    loadData = loadData;
    loadIcon = loadIcon;
    container = container$1;
    baseboardContainer = baseboardContainer$1;
    currentProvider;
    textStyle;
    containerStyle;
    initStyle() {
        this.textStyle = {
            fontSize: 18,
            fontWeight: 'bold'
        };
        this.containerStyle = {
            width: 400
        };
    }
    constructor(store) {
        this.store = store;
        this.initStyle();
        this.currentProvider = getLeafProviderInfo(this.store.getSelectedProvider()).providerName;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Load, deps: [{ token: LeafStore }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Load, selector: "load", ngImport: i0, template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n    <div [ngClass]=\"loadData\">\n        <load-icon [ngClass]=\"loadIcon\" alt=\"load-icon\" />\n        <p [ngClass]=\"simpleText(textStyle)\">Authenticating with {{currentProvider}}</p>\n    </div>\n    <div [ngClass]=\"baseboardContainer\">\n        <baseboard />\n    </div>\n</div>", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: Baseboard, selector: "baseboard" }, { kind: "component", type: LoadIcon, selector: "load-icon" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Load, decorators: [{
            type: Component,
            args: [{ selector: 'load', template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n    <div [ngClass]=\"loadData\">\n        <load-icon [ngClass]=\"loadIcon\" alt=\"load-icon\" />\n        <p [ngClass]=\"simpleText(textStyle)\">Authenticating with {{currentProvider}}</p>\n    </div>\n    <div [ngClass]=\"baseboardContainer\">\n        <baseboard />\n    </div>\n</div>" }]
        }], ctorParameters: function () { return [{ type: LeafStore }]; } });
;

const organizationTitle = css `
    display: flex;
    align-items: center;
    margin: 0px 0px 0px 30px;
`;
const providerLogo = css `
    width: 40px;
    height: 40px;
    border-radius: 5px;
`;
const providerData = css `
    margin: 0px 0px 0px 10px;
`;
const breadCrumb = css `
    margin: 0px 0px 0px 0px;
`;
const organizationBody = css `
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 20px 0px 0px 0px;
`;
const organizationCardContainer = css `
    width: 390px;
    height: 210px;
    margin: 20px 0px 0px 0px;
`;
const card = css `
    margin: 0px 0px 15px 0px;
`;
const organizationFooter = css `
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin: 10px 25px 0px 0px;
`;
const baseboardContainer = css `
    display: flex;
    justify-content: flex-end;
    margin: 0px 0px 0px 0px;
`;

const organizationCard = css `
    display: flex;
    align-items: center;
    height: 50px;
    border: 1px solid #7E7E7E;
    border-radius: 5px;
    padding: 0px 5px 0px 10px;
    margin: 0px 5px 15px 25px;
`;
const organizationName = css `
    margin-right: auto;
`;

class OrganizationCard {
    globalStyle = globalStyle;
    organizationCard = organizationCard;
    organizationName = organizationName;
    simpleText = simpleText;
    organization;
    tagProps;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OrganizationCard, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: OrganizationCard, selector: "organization-card", inputs: { organization: "organization", tagProps: "tagProps" }, ngImport: i0, template: "<div [ngClass]=\"[globalStyle, organizationCard]\">\n    <p [ngClass]=\"organizationName\">{{organization}}</p>\n    <tag [props]=\"tagProps\"></tag>\n</div>", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: Tag, selector: "tag", inputs: ["props"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OrganizationCard, decorators: [{
            type: Component,
            args: [{ selector: 'organization-card', template: "<div [ngClass]=\"[globalStyle, organizationCard]\">\n    <p [ngClass]=\"organizationName\">{{organization}}</p>\n    <tag [props]=\"tagProps\"></tag>\n</div>" }]
        }], propDecorators: { organization: [{
                type: Input
            }], tagProps: [{
                type: Input
            }] } });
;

class Organization {
    store;
    leafProviderData;
    screenProvider;
    usermanagementService;
    i18n;
    translate;
    globalStyle = globalStyle;
    simpleText = simpleText;
    scrollable = scrollableContainer;
    container = container$1;
    organizationTitle = organizationTitle;
    providerLogo = providerLogo;
    providerData = providerData;
    breadCrumb = breadCrumb;
    organizationBody = organizationBody;
    organizationCardContainer = organizationCardContainer;
    card = card;
    organizationFooter = organizationFooter;
    baseboardContainer = baseboardContainer;
    providerNameStyle;
    almostDoneStyle;
    almostDoneDescStyle;
    allowButtonStyle;
    doneButtonStyle;
    containerStyle;
    apiKey;
    leafUser;
    lang;
    getWidgetStatus = new EventEmitter();
    currentProvider;
    providerName;
    providerLogoUrl;
    haveOrganizationConnected;
    organizations;
    allowUrl;
    interval;
    initStyle() {
        this.providerNameStyle = {
            fontSize: 18,
            fontWeight: 'bold',
            margin: '15px 0px 0px 0px',
        };
        this.almostDoneStyle = {
            fontSize: 20,
        };
        this.almostDoneDescStyle = {
            fontSize: 12,
            margin: '-5px 0px 15px 0px',
        };
        this.allowButtonStyle = {
            padding: '0px 15px 0px 15px',
            backgroundColor: 'white',
            color: '#0f876d',
            fontSize: 14,
            height: 30,
            iconUrl: 'https://d2bkrhd64jf4kw.cloudfront.net/assets/icons/arrow-up-right-from-square-solid.svg'
        };
        this.doneButtonStyle = {
            padding: '0px 20px 0px 20px',
            backgroundColor: '#0f876d',
            color: 'white',
            fontSize: 18,
            height: 40,
        };
        this.containerStyle = {
            width: 400,
        };
    }
    constructor(store, leafProviderData, screenProvider, usermanagementService, i18n, translate) {
        this.store = store;
        this.leafProviderData = leafProviderData;
        this.screenProvider = screenProvider;
        this.usermanagementService = usermanagementService;
        this.i18n = i18n;
        this.translate = translate;
        this.initStyle();
        const providerInfo = getLeafProviderInfo(this.store.getSelectedProvider());
        this.providerName = providerInfo.providerName;
        this.providerLogoUrl = providerInfo.providerLogo;
        this.currentProvider = providerInfo.provider;
    }
    ngOnInit() {
        this.checkOrganizations()
            .catch(() => this.leafProviderData.setProviderWidgetStatus({
            code: -1,
            message: 'Error',
        }))
            .finally(() => {
            this.getWidgetStatus.emit({
                providersConnected: this.leafProviderData.providersConnected,
                providerWidgetStatus: this.leafProviderData.providerWidgetStatus,
            });
        });
        this.i18n.init(this.translate, this.lang);
    }
    ngOnChanges() {
        this.i18n.changeLanguage(this.lang);
    }
    handleDoneButtonClick() {
        clearInterval(this.interval);
        this.screenProvider.screenTransition('done');
    }
    async checkOrganizations() {
        const newOrganizations = [];
        const { connectedOrganizations, notConnectedOrganizations, managementUri } = await this.usermanagementService.getOrganizations(this.currentProvider ?? '', this.apiKey, this.leafUser);
        this.haveOrganizationConnected = connectedOrganizations.length > 0;
        connectedOrganizations
            .map((organization) => {
            return {
                id: organization.id,
                name: organization.name,
                allowed: true,
                tag: TagValuesProps.ORGANIZATION_ALLOWED,
            };
        })
            .forEach((organization) => newOrganizations.push(organization));
        if (notConnectedOrganizations) {
            notConnectedOrganizations
                .map((organization) => {
                return {
                    id: organization.id,
                    name: organization.name,
                    allowed: false,
                    tag: TagValuesProps.ORGANIZATION_NOT_ALLOWED,
                };
            })
                .forEach((organization) => newOrganizations.push(organization));
        }
        if (newOrganizations != this.organizations) {
            this.organizations = newOrganizations;
        }
        if (managementUri) {
            this.allowUrl = managementUri;
        }
        else {
            const url = this.buildAllowUrl(connectedOrganizations, notConnectedOrganizations);
            this.allowUrl = url;
        }
    }
    buildAllowUrl(connectedOrganizations, notConnectedOrganizations) {
        if (notConnectedOrganizations?.length) {
            return notConnectedOrganizations.shift()?.managementUri ?? '';
        }
        return connectedOrganizations.shift()?.managementUri ?? '';
    }
    async allowOrganizations() {
        window.open(this.allowUrl);
        this.interval = setInterval(() => {
            this.checkOrganizations()
                .catch(() => this.leafProviderData.setProviderWidgetStatus({
                code: -1,
                message: 'Error',
            }))
                .finally(() => {
                this.getWidgetStatus.emit({
                    providersConnected: this.leafProviderData.providersConnected,
                    providerWidgetStatus: this.leafProviderData.providerWidgetStatus,
                });
            });
        }, Number(environment.INTERVAL_IN_MILLIS));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Organization, deps: [{ token: LeafStore }, { token: LeafProviderData }, { token: ScreenProvider }, { token: UsermanagementService }, { token: I18n }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Organization, selector: "organization", inputs: { apiKey: "apiKey", leafUser: "leafUser", lang: "lang" }, outputs: { getWidgetStatus: "getWidgetStatus" }, usesOnChanges: true, ngImport: i0, template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <leaf-header></leaf-header>\n  <div [ngClass]=\"organizationTitle\">\n    <img\n      data-testid=\"provider-logo\"\n      [ngClass]=\"providerLogo\"\n      [src]=\"providerLogoUrl\"\n      alt=\"provider-logo\"\n    />\n    <div [ngClass]=\"providerData\">\n      <p data-testid=\"provider-name\" [ngClass]=\"simpleText(providerNameStyle)\">\n        {{ providerName }}\n      </p>\n    </div>\n  </div>\n  <div [ngClass]=\"organizationBody\">\n    <p data-testid=\"almost-done\" [ngClass]=\"simpleText(almostDoneStyle)\">\n      {{ \"screens.organization.almostDone\" | translate }}\n    </p>\n    <p\n      data-testid=\"almost-done-desc\"\n      [ngClass]=\"simpleText(almostDoneDescStyle)\"\n    >\n      {{ \"screens.organization.pleaseAllow\" | translate }}\n    </p>\n    <custom-button\n      data-testid=\"allow-button\"\n      [buttonStyle]=\"allowButtonStyle\"\n      text=\"{{ 'screens.organization.allow' | translate }}\"\n      (click)=\"allowOrganizations()\"\n    ></custom-button>\n  </div>\n  <div [ngClass]=\"[scrollable, organizationCardContainer]\">\n    <div *ngFor=\"let organization of organizations\">\n      <organization-card\n        [organization]=\"organization.name\"\n        [tagProps]=\"organization.tag\"\n      ></organization-card>\n    </div>\n  </div>\n  <div [ngClass]=\"organizationFooter\">\n    <custom-button\n      data-testid=\"done-button\"\n      [buttonStyle]=\"doneButtonStyle\"\n      text=\"{{ 'screens.organization.done' | translate }}\"\n      (click)=\"handleDoneButtonClick()\"\n    ></custom-button>\n  </div>\n  <div [ngClass]=\"baseboardContainer\">\n    <baseboard />\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: LeafHeader, selector: "leaf-header" }, { kind: "component", type: CustomButton, selector: "custom-button", inputs: ["text", "buttonStyle"] }, { kind: "component", type: OrganizationCard, selector: "organization-card", inputs: ["organization", "tagProps"] }, { kind: "component", type: Baseboard, selector: "baseboard" }, { kind: "pipe", type: i1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Organization, decorators: [{
            type: Component,
            args: [{ selector: 'organization', template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <leaf-header></leaf-header>\n  <div [ngClass]=\"organizationTitle\">\n    <img\n      data-testid=\"provider-logo\"\n      [ngClass]=\"providerLogo\"\n      [src]=\"providerLogoUrl\"\n      alt=\"provider-logo\"\n    />\n    <div [ngClass]=\"providerData\">\n      <p data-testid=\"provider-name\" [ngClass]=\"simpleText(providerNameStyle)\">\n        {{ providerName }}\n      </p>\n    </div>\n  </div>\n  <div [ngClass]=\"organizationBody\">\n    <p data-testid=\"almost-done\" [ngClass]=\"simpleText(almostDoneStyle)\">\n      {{ \"screens.organization.almostDone\" | translate }}\n    </p>\n    <p\n      data-testid=\"almost-done-desc\"\n      [ngClass]=\"simpleText(almostDoneDescStyle)\"\n    >\n      {{ \"screens.organization.pleaseAllow\" | translate }}\n    </p>\n    <custom-button\n      data-testid=\"allow-button\"\n      [buttonStyle]=\"allowButtonStyle\"\n      text=\"{{ 'screens.organization.allow' | translate }}\"\n      (click)=\"allowOrganizations()\"\n    ></custom-button>\n  </div>\n  <div [ngClass]=\"[scrollable, organizationCardContainer]\">\n    <div *ngFor=\"let organization of organizations\">\n      <organization-card\n        [organization]=\"organization.name\"\n        [tagProps]=\"organization.tag\"\n      ></organization-card>\n    </div>\n  </div>\n  <div [ngClass]=\"organizationFooter\">\n    <custom-button\n      data-testid=\"done-button\"\n      [buttonStyle]=\"doneButtonStyle\"\n      text=\"{{ 'screens.organization.done' | translate }}\"\n      (click)=\"handleDoneButtonClick()\"\n    ></custom-button>\n  </div>\n  <div [ngClass]=\"baseboardContainer\">\n    <baseboard />\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: LeafStore }, { type: LeafProviderData }, { type: ScreenProvider }, { type: UsermanagementService }, { type: I18n }, { type: i1.TranslateService }]; }, propDecorators: { apiKey: [{
                type: Input
            }], leafUser: [{
                type: Input
            }], lang: [{
                type: Input
            }], getWidgetStatus: [{
                type: Output
            }] } });

const doneHeader = css `
    margin-bottom: 10px;
`;
const doneProviderData = css `
    display: flex;
    align-items: center;
`;
const doneProviderImage = css `
    height: 50px;
    width: 50px;
    margin: 0px 20px 0px 40px;
    border-radius: 5px;
`;
const doneProviderBody = css `
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 100px;
`;
const allDoneText = css `
    margin-bottom: 0px;
`;
const doneFooter = css `
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
`;
const doneButton = css `
    margin: 30px 30px -15px auto;
`;
const poweredStyle = css `
    margin: 0px 80px 0px auto;
`;

class Done {
    screenProvider;
    store;
    leafProviderData;
    usermanagementService;
    i18n;
    translate;
    globalStyle = globalStyle;
    simpleText = simpleText;
    container = container$1;
    doneHeader = doneHeader;
    doneProviderImage = doneProviderImage;
    doneProviderData = doneProviderData;
    doneProviderBody = doneProviderBody;
    doneButton = doneButton;
    doneFooter = doneFooter;
    poweredStyle = poweredStyle;
    allDoneText = allDoneText;
    providerNameStyle;
    allDoneStyle;
    allDoneDescriptionStyle;
    doneButtonStyle;
    addOtherProviderButtonStyle;
    containerStyle;
    apiKey;
    leafUser;
    lang;
    getWidgetStatus = new EventEmitter();
    currentProvider;
    providerName;
    providerLogo;
    initStyle() {
        this.providerNameStyle = {
            fontSize: 20,
            fontWeight: 'bold',
        };
        this.allDoneStyle = {
            fontSize: 36,
        };
        this.allDoneDescriptionStyle = {
            fontSize: 12,
        };
        this.doneButtonStyle = {
            padding: '0px 30px 0px 30px',
            backgroundColor: '#0f876d',
            color: 'white',
            fontSize: 18,
            height: 40,
        };
        this.addOtherProviderButtonStyle = {
            padding: '0px 25px 0px 25px',
            backgroundColor: 'white',
            color: '#0f876d',
            fontSize: 18,
            height: 40,
        };
        this.containerStyle = {
            width: 400,
        };
    }
    constructor(screenProvider, store, leafProviderData, usermanagementService, i18n, translate) {
        this.screenProvider = screenProvider;
        this.store = store;
        this.leafProviderData = leafProviderData;
        this.usermanagementService = usermanagementService;
        this.i18n = i18n;
        this.translate = translate;
        this.initStyle();
        const providerInfo = getLeafProviderInfo(this.store.getSelectedProvider());
        this.providerName = providerInfo.providerName;
        this.providerLogo = providerInfo.providerLogo;
        this.currentProvider = providerInfo.provider;
    }
    ngOnInit() {
        this.leafProviderData.providersConnected &&
            this.newProviderConnected()
                .catch(() => this.leafProviderData.setProviderWidgetStatus({
                code: -1,
                message: 'Error',
            }))
                .finally(() => {
                this.getWidgetStatus.emit({
                    providersConnected: this.leafProviderData.providersConnected,
                    providerWidgetStatus: this.leafProviderData.providerWidgetStatus,
                });
            });
        this.i18n.init(this.translate, this.lang);
    }
    ngOnChanges() {
        this.i18n.changeLanguage(this.lang);
    }
    async newProviderConnected() {
        const connectedProviders = await this.usermanagementService.leafProviderConnByLeafUser(this.apiKey, this.leafUser);
        const newProvider = connectedProviders.find((connectedProvider) => connectedProvider === this.currentProvider);
        if (newProvider) {
            this.leafProviderData.setProvidersConnected({
                provider: newProvider,
                createdTime: '',
            });
        }
    }
    handleDoneButtonClick() {
        this.screenProvider.screenTransition('warning');
    }
    handleAddOtherButtonClick() {
        this.screenProvider.screenTransition('list');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Done, deps: [{ token: ScreenProvider }, { token: LeafStore }, { token: LeafProviderData }, { token: UsermanagementService }, { token: I18n }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Done, selector: "done", inputs: { apiKey: "apiKey", leafUser: "leafUser", lang: "lang" }, outputs: { getWidgetStatus: "getWidgetStatus" }, usesOnChanges: true, ngImport: i0, template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <div [ngClass]=\"doneHeader\">\n    <leaf-header></leaf-header>\n  </div>\n  <div [ngClass]=\"doneProviderData\">\n    <img\n      data-testid=\"provider-image\"\n      [ngClass]=\"doneProviderImage\"\n      [src]=\"providerLogo\"\n      alt=\"provider-logo\"\n    />\n    <p data-testid=\"provider-name\" [ngClass]=\"simpleText(providerNameStyle)\">\n      {{ providerName }}\n    </p>\n  </div>\n  <div [ngClass]=\"doneProviderBody\">\n    <p\n      data-testid=\"allDone-text\"\n      [ngClass]=\"[simpleText(allDoneStyle), allDoneText]\"\n    >\n      {{ \"screens.done.allDone\" | translate }}\n    </p>\n    <p\n      data-testid=\"allDone-desc\"\n      [ngClass]=\"simpleText(allDoneDescriptionStyle)\"\n    >\n      Operations Center {{ \"screens.done.created\" | translate }}\n    </p>\n    <custom-button\n      data-testid=\"add-button\"\n      [buttonStyle]=\"addOtherProviderButtonStyle\"\n      text=\"{{ 'screens.done.add' | translate }}\"\n      (click)=\"handleAddOtherButtonClick()\"\n    ></custom-button>\n  </div>\n  <div [ngClass]=\"doneFooter\">\n    <custom-button\n      [ngClass]=\"doneButton\"\n      data-testid=\"done-button\"\n      [buttonStyle]=\"doneButtonStyle\"\n      text=\"{{ 'screens.done.done' | translate }}\"\n      (click)=\"handleDoneButtonClick()\"\n    ></custom-button>\n    <baseboard />\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: LeafHeader, selector: "leaf-header" }, { kind: "component", type: CustomButton, selector: "custom-button", inputs: ["text", "buttonStyle"] }, { kind: "component", type: Baseboard, selector: "baseboard" }, { kind: "pipe", type: i1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Done, decorators: [{
            type: Component,
            args: [{ selector: 'done', template: "<div [ngClass]=\"[globalStyle, container(containerStyle)]\">\n  <div [ngClass]=\"doneHeader\">\n    <leaf-header></leaf-header>\n  </div>\n  <div [ngClass]=\"doneProviderData\">\n    <img\n      data-testid=\"provider-image\"\n      [ngClass]=\"doneProviderImage\"\n      [src]=\"providerLogo\"\n      alt=\"provider-logo\"\n    />\n    <p data-testid=\"provider-name\" [ngClass]=\"simpleText(providerNameStyle)\">\n      {{ providerName }}\n    </p>\n  </div>\n  <div [ngClass]=\"doneProviderBody\">\n    <p\n      data-testid=\"allDone-text\"\n      [ngClass]=\"[simpleText(allDoneStyle), allDoneText]\"\n    >\n      {{ \"screens.done.allDone\" | translate }}\n    </p>\n    <p\n      data-testid=\"allDone-desc\"\n      [ngClass]=\"simpleText(allDoneDescriptionStyle)\"\n    >\n      Operations Center {{ \"screens.done.created\" | translate }}\n    </p>\n    <custom-button\n      data-testid=\"add-button\"\n      [buttonStyle]=\"addOtherProviderButtonStyle\"\n      text=\"{{ 'screens.done.add' | translate }}\"\n      (click)=\"handleAddOtherButtonClick()\"\n    ></custom-button>\n  </div>\n  <div [ngClass]=\"doneFooter\">\n    <custom-button\n      [ngClass]=\"doneButton\"\n      data-testid=\"done-button\"\n      [buttonStyle]=\"doneButtonStyle\"\n      text=\"{{ 'screens.done.done' | translate }}\"\n      (click)=\"handleDoneButtonClick()\"\n    ></custom-button>\n    <baseboard />\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: ScreenProvider }, { type: LeafStore }, { type: LeafProviderData }, { type: UsermanagementService }, { type: I18n }, { type: i1.TranslateService }]; }, propDecorators: { apiKey: [{
                type: Input
            }], leafUser: [{
                type: Input
            }], lang: [{
                type: Input
            }], getWidgetStatus: [{
                type: Output
            }] } });

class Providers {
    screenProvider;
    companyName;
    companyLogo;
    apiKey;
    leafUser;
    lang;
    getWidgetStatus = new EventEmitter();
    currentScreen;
    screenChangeSubscription;
    constructor(screenProvider) {
        this.screenProvider = screenProvider;
    }
    ngOnInit() {
        this.currentScreen = localStorage.getItem('dismissWarningScreen') !== 'true' ? 'warning' : 'list';
        this.screenChangeSubscription = this.screenProvider.screenChangeObservable.subscribe(() => {
            this.currentScreen = this.screenProvider.getCurrentScreen();
        });
    }
    ngOnDestroy() {
        this.screenChangeSubscription.unsubscribe();
    }
    handleGetWidgetStatus(value) {
        this.getWidgetStatus.emit(value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Providers, deps: [{ token: ScreenProvider }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Providers, selector: "providers", inputs: { companyName: "companyName", companyLogo: "companyLogo", apiKey: "apiKey", leafUser: "leafUser", lang: "lang" }, outputs: { getWidgetStatus: "getWidgetStatus" }, ngImport: i0, template: "<ng-container [ngSwitch]=\"currentScreen\">\n    <warning\n      *ngSwitchCase=\"'warning'\"\n      [companyName]=\"companyName\"\n      [companyLogo]=\"companyLogo\"\n      [widget]=\"'providers'\"\n      [lang]=\"lang\"\n    ></warning>\n  \n    <list\n      *ngSwitchCase=\"'list'\"\n      [apiKey]=\"apiKey\"\n      [leafUser]=\"leafUser\"\n      (getWidgetStatus)=\"handleGetWidgetStatus($event)\"\n    ></list>\n\n    <provider\n      *ngSwitchCase=\"'provider'\"\n      [companyName]=\"companyName\"\n      [apiKey]=\"apiKey\"\n      [leafUser]=\"leafUser\"\n    ></provider>\n\n    <load\n      *ngSwitchCase=\"'load'\"\n    ></load>\n\n    <organization\n      *ngSwitchCase=\"'organizations'\"\n      [apiKey]=\"apiKey\"\n      [leafUser]=\"leafUser\"\n      (getWidgetStatus)=\"handleGetWidgetStatus($event)\"\n    ></organization>\n    \n    <done\n      *ngSwitchCase=\"'done'\"\n      [apiKey]=\"apiKey\"\n      [leafUser]=\"leafUser\"\n      (getWidgetStatus)=\"handleGetWidgetStatus($event)\"\n    ></done>\n  </ng-container>", dependencies: [{ kind: "directive", type: i1$1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: List, selector: "list", inputs: ["apiKey", "leafUser", "lang"], outputs: ["getWidgetStatus"] }, { kind: "component", type: Provider, selector: "provider", inputs: ["companyName", "apiKey", "leafUser", "lang"] }, { kind: "component", type: Load, selector: "load" }, { kind: "component", type: Organization, selector: "organization", inputs: ["apiKey", "leafUser", "lang"], outputs: ["getWidgetStatus"] }, { kind: "component", type: Done, selector: "done", inputs: ["apiKey", "leafUser", "lang"], outputs: ["getWidgetStatus"] }, { kind: "component", type: Warning, selector: "warning", inputs: ["companyName", "companyLogo", "widget", "lang"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Providers, decorators: [{
            type: Component,
            args: [{ selector: 'providers', template: "<ng-container [ngSwitch]=\"currentScreen\">\n    <warning\n      *ngSwitchCase=\"'warning'\"\n      [companyName]=\"companyName\"\n      [companyLogo]=\"companyLogo\"\n      [widget]=\"'providers'\"\n      [lang]=\"lang\"\n    ></warning>\n  \n    <list\n      *ngSwitchCase=\"'list'\"\n      [apiKey]=\"apiKey\"\n      [leafUser]=\"leafUser\"\n      (getWidgetStatus)=\"handleGetWidgetStatus($event)\"\n    ></list>\n\n    <provider\n      *ngSwitchCase=\"'provider'\"\n      [companyName]=\"companyName\"\n      [apiKey]=\"apiKey\"\n      [leafUser]=\"leafUser\"\n    ></provider>\n\n    <load\n      *ngSwitchCase=\"'load'\"\n    ></load>\n\n    <organization\n      *ngSwitchCase=\"'organizations'\"\n      [apiKey]=\"apiKey\"\n      [leafUser]=\"leafUser\"\n      (getWidgetStatus)=\"handleGetWidgetStatus($event)\"\n    ></organization>\n    \n    <done\n      *ngSwitchCase=\"'done'\"\n      [apiKey]=\"apiKey\"\n      [leafUser]=\"leafUser\"\n      (getWidgetStatus)=\"handleGetWidgetStatus($event)\"\n    ></done>\n  </ng-container>" }]
        }], ctorParameters: function () { return [{ type: ScreenProvider }]; }, propDecorators: { companyName: [{
                type: Input
            }], companyLogo: [{
                type: Input
            }], apiKey: [{
                type: Input
            }], leafUser: [{
                type: Input
            }], lang: [{
                type: Input
            }], getWidgetStatus: [{
                type: Output
            }] } });
;

class ProviderCardModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProviderCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: ProviderCardModule, declarations: [ProviderCard], imports: [CommonModule,
            TagModule], exports: [ProviderCard] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProviderCardModule, imports: [CommonModule,
            TagModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProviderCardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ProviderCard,
                    ],
                    imports: [
                        CommonModule,
                        TagModule
                    ],
                    exports: [
                        ProviderCard
                    ]
                }]
        }] });

class BackButtonModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BackButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: BackButtonModule, declarations: [BackButton], imports: [CommonModule], exports: [BackButton] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BackButtonModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BackButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        BackButton,
                    ],
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        BackButton
                    ]
                }]
        }] });

class LeafHeaderModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: LeafHeaderModule, declarations: [LeafHeader], imports: [CommonModule,
            LeafLogoModule,
            BackButtonModule], exports: [LeafHeader] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafHeaderModule, imports: [CommonModule,
            LeafLogoModule,
            BackButtonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LeafHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        LeafHeader
                    ],
                    imports: [
                        CommonModule,
                        LeafLogoModule,
                        BackButtonModule
                    ],
                    exports: [
                        LeafHeader
                    ]
                }]
        }] });

class SearchBarModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: SearchBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: SearchBarModule, declarations: [SearchBar], imports: [CommonModule, i1.TranslateModule], exports: [SearchBar] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: SearchBarModule, imports: [CommonModule,
            TranslateModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: SearchBarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SearchBar,
                    ],
                    imports: [
                        CommonModule,
                        TranslateModule.forRoot(),
                    ],
                    exports: [
                        SearchBar
                    ]
                }]
        }] });

class ListModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: ListModule, declarations: [List], imports: [CommonModule,
            LeafHeaderModule,
            ProviderCardModule,
            SearchBarModule,
            BaseboardModule, i1.TranslateModule], exports: [List] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ListModule, providers: [ScreenProvider, I18n], imports: [CommonModule,
            LeafHeaderModule,
            ProviderCardModule,
            SearchBarModule,
            BaseboardModule,
            TranslateModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [List],
                    imports: [
                        CommonModule,
                        LeafHeaderModule,
                        ProviderCardModule,
                        SearchBarModule,
                        BaseboardModule,
                        TranslateModule.forRoot(),
                    ],
                    providers: [ScreenProvider, I18n],
                    exports: [List],
                }]
        }] });

class ProviderModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProviderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: ProviderModule, declarations: [Provider], imports: [CommonModule,
            LeafHeaderModule,
            CustomButtonModule, i1.TranslateModule], exports: [Provider] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProviderModule, providers: [ScreenProvider], imports: [CommonModule,
            LeafHeaderModule,
            CustomButtonModule,
            TranslateModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProviderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Provider,
                    ],
                    imports: [
                        CommonModule,
                        LeafHeaderModule,
                        CustomButtonModule,
                        TranslateModule.forRoot(),
                    ],
                    providers: [ScreenProvider],
                    exports: [
                        Provider
                    ]
                }]
        }] });

class LoadModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LoadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: LoadModule, declarations: [Load,
            LoadIcon], imports: [CommonModule,
            BaseboardModule], exports: [Load] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LoadModule, imports: [CommonModule,
            BaseboardModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: LoadModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Load,
                        LoadIcon
                    ],
                    imports: [
                        CommonModule,
                        BaseboardModule,
                    ],
                    exports: [
                        Load
                    ]
                }]
        }] });

class OrganizationCardModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OrganizationCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: OrganizationCardModule, declarations: [OrganizationCard], imports: [CommonModule,
            TagModule], exports: [OrganizationCard] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OrganizationCardModule, imports: [CommonModule,
            TagModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OrganizationCardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        OrganizationCard,
                    ],
                    imports: [
                        CommonModule,
                        TagModule
                    ],
                    exports: [
                        OrganizationCard
                    ]
                }]
        }] });

const breadcrumb = css `
	display: flex;
`;
const breadCrumbList = (current) => css `
	list-style: none;
	font-size: 12px;
	cursor: pointer;

	&+:before {
		padding: 10px;
		color: #4d4d4d;
		content: ">";
	}

	${current ? `
		font-weight: bold;
		color: black;
		text-decoration: none;

	` :
    `
		color: #b1b1b1;
		text-decoration: none;

	`}
`;

class Breadcrumb {
    globalStyle = globalStyle;
    breadcrumb = breadcrumb;
    breadcrumbList = breadCrumbList;
    pages;
    constructor() {
        this.pages = [
            { label: 'Authentication', current: false },
            { label: 'Permissions', current: false },
            { label: 'Organizations', current: true }
        ];
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Breadcrumb, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.6", type: Breadcrumb, selector: "breadcrumb", ngImport: i0, template: "<div [ngClass]=\"breadcrumb\">\n\t<li *ngFor=\"let page of pages\" [ngClass]=\"breadcrumbList(page.current)\">{{page.label}}</li>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: Breadcrumb, decorators: [{
            type: Component,
            args: [{ selector: 'breadcrumb', template: "<div [ngClass]=\"breadcrumb\">\n\t<li *ngFor=\"let page of pages\" [ngClass]=\"breadcrumbList(page.current)\">{{page.label}}</li>\n</div>\n" }]
        }], ctorParameters: function () { return []; } });
;

class BreadcrumbModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BreadcrumbModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: BreadcrumbModule, declarations: [Breadcrumb], imports: [CommonModule], exports: [Breadcrumb] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BreadcrumbModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: BreadcrumbModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Breadcrumb,
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        Breadcrumb
                    ]
                }]
        }] });

class OrganizationModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OrganizationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: OrganizationModule, declarations: [Organization], imports: [CommonModule,
            LeafHeaderModule,
            CustomButtonModule,
            OrganizationCardModule,
            BreadcrumbModule,
            BaseboardModule, i1.TranslateModule], exports: [Organization] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OrganizationModule, providers: [ScreenProvider], imports: [CommonModule,
            LeafHeaderModule,
            CustomButtonModule,
            OrganizationCardModule,
            BreadcrumbModule,
            BaseboardModule,
            TranslateModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: OrganizationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Organization,
                    ],
                    imports: [
                        CommonModule,
                        LeafHeaderModule,
                        CustomButtonModule,
                        OrganizationCardModule,
                        BreadcrumbModule,
                        BaseboardModule,
                        TranslateModule.forRoot(),
                    ],
                    providers: [ScreenProvider],
                    exports: [
                        Organization
                    ]
                }]
        }] });

class DoneModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: DoneModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: DoneModule, declarations: [Done], imports: [CommonModule,
            LeafHeaderModule,
            CustomButtonModule,
            BaseboardModule, i1.TranslateModule], exports: [Done] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: DoneModule, providers: [ScreenProvider], imports: [CommonModule,
            LeafHeaderModule,
            CustomButtonModule,
            BaseboardModule,
            TranslateModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: DoneModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Done,
                    ],
                    imports: [
                        CommonModule,
                        LeafHeaderModule,
                        CustomButtonModule,
                        BaseboardModule,
                        TranslateModule.forRoot(),
                    ],
                    providers: [ScreenProvider],
                    exports: [
                        Done
                    ]
                }]
        }] });

class ProvidersModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProvidersModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.6", ngImport: i0, type: ProvidersModule, declarations: [Providers], imports: [CommonModule,
            ListModule,
            ProviderModule,
            LoadModule,
            OrganizationModule,
            DoneModule,
            WarningModule], exports: [Providers] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProvidersModule, imports: [CommonModule,
            ListModule,
            ProviderModule,
            LoadModule,
            OrganizationModule,
            DoneModule,
            WarningModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.6", ngImport: i0, type: ProvidersModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        Providers,
                    ],
                    imports: [
                        CommonModule,
                        ListModule,
                        ProviderModule,
                        LoadModule,
                        OrganizationModule,
                        DoneModule,
                        WarningModule
                    ],
                    exports: [
                        Providers
                    ]
                }]
        }] });

/*
 * Public API Surface of leaf-link-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FileUpload, FileUploadModule, Providers, ProvidersModule };
